/**
 * The explorations sidebar — the left pane, and what the corner's one capsule
 * grew into.
 *
 * D18 deleted a left rail and D22 folded its daily jobs into a single pill, both
 * for the same reason: a column that only listed what was stored had not earned
 * one. This pane carries four jobs a 326px dropdown could not — explorations
 * grouped by PROJECT, a search over that index, a fork on every row, and the
 * name the person working here goes by — and a list you file, search and copy
 * from is a place rather than a menu.
 *
 * IT IS THE APP'S FRONT DOOR NOW, and it is laid out as one (D89, D102): the
 * app's own name at the top, with the search's magnifying glass and the
 * collapse button at its right; under it NEW EXPLORATION, the one maker, and
 * then the three DOORS that are about the workspace rather than the list —
 * Research Lab, your own sources, the library — as one column of rows; and
 * then the list itself, in sections: PINNED, the rows somebody keeps at hand,
 * when there are any; PROJECTS, always, because its eyebrow carries the + that
 * makes a project, and under it each project as a folder-led heading with its
 * rows set in under it, a + that starts a new exploration in that project,
 * born filed, and a heading — folder and name — that folds the rows away and
 * opens them again (D101, D368), with a menu of its own on a right-click; and
 * RECENT, the unfiled rows, last. The search is not in the column
 * at all: the glass opens a sheet that lists what matches (`ExplorationSearch`),
 * so nothing here filters. The reference the owner pointed at is the shape of a
 * coding agent's sidebar; what was taken from it is the ORDER and the
 * hierarchy, and what was not is anything the model here cannot say — see the
 * decisions for the line between the two.
 *
 * A PIN IS A BOOKMARK ON THE LIST, NOT A FILING (D99). D89 declined the
 * reference's Pinned section because nothing here could be pinned and a
 * heading over an empty set names nothing; the second half still holds, so the
 * section draws only while something is in it. A pinned row is drawn AGAIN in
 * its project — its group, its count and its label are untouched — and can be
 * dragged into a project from either place. The section itself is not a
 * target: a drop over it is a cancel, like a drop on the doors above the list. The ids
 * live in the codec's own table (`core/pins.ts` says why there and not on the
 * document), newest pin first, and the one door that takes a pin down is the
 * one that takes the exploration down.
 *
 * EVERY ACT ON A ROW IS BEHIND ITS ⋮ (D100). One control at the row's tail
 * opens a menu — Open, Pin or Unpin, Fork, Share or export, Move to project as
 * a submenu of the groups on screen, and Forget last — where the row used to
 * carry four glyphs. `rowMenu.ts` holds what it lists and where it opens; the
 * menu itself goes through the ghost's portal, for the ghost's reason and one
 * more: the list scrolls, and a menu inside it would be clipped on exactly the
 * rows a long list puts at the bottom. A focused row still answers the menu's
 * letters without opening it — P, F, S, and M for the lift — and ⇧F10 or the
 * Menu key opens it from the keyboard. A right-click opens it too, at the
 * pointer (D335, D368), and a right-click on a project's heading opens the same
 * menu over the acts on the project: make, fold, rename, remove.
 *
 * FILING IS A MOVEMENT HERE, not a form. A row is dragged into a group, or
 * lifted with `M` and dropped there by the keyboard; both land on `fileRow` and
 * therefore on the same two doors the rest of the app files through. D56 records
 * why the panel this replaced was the wrong frame for a list of things you move
 * between headings, and `fileDrag.ts` holds every decision either hand makes.
 *
 * It sits INSIDE the stage on the Thread's own `GlassPane` shell — docked, a
 * column on its own ground (D353); on a phone, glass over the live graph — so
 * `useModalFocus`'s single `main.stage` inert flag still puts it behind every
 * sheet. What it takes from the field it declares: App turns its width into
 * `chromeLeft` and `viewInset.left`, so the session column, the camera, the
 * captions, the pins and the coach's card all keep out from under it rather
 * than each guessing a corner.
 *
 * THE LIST IS READ AT A SIGNAL, NEVER SUBSCRIBED PER NODE. `savedAt` (the
 * autosave publishes it), a counter this file bumps when it writes the index
 * itself, the open session's id, and the forget registry's announcements — a
 * Forget from a door outside this pane writes the index only once its delete
 * commits, after every other signal has already fired (D331, D342). A pane
 * keyed on `rev` would regroup twelve rows for every node a run threads, for a
 * list nobody is reading during a run.
 * The rows print no age and no node count (D102), so there is no clock here
 * either — nothing on a row goes stale without a write. A row is NAMED by the
 * name somebody gave it, or else by its seed clamped the way a project label is
 * (`core/name.ts`, D105), the whole seed a hover away on its title, and
 * everything here that names a row — the row, its ghost, its menu, its confirm,
 * its name field, the lift's sentence — names it through `nameOf`, as the tab's
 * title does through the `shownName` under it. A fork says where it came from
 * under the name, as a dated fact rather than an age, so that line does not go
 * stale either.
 *
 * Below 768px it is not in flow at all — a 40×40 opener where the pill sat, and
 * an overlay when pressed. Expanding it while a run is live is REFUSED: at that
 * width the overlay would cover the pod's Stop and "Not there", and Escape is
 * deliberately not allowed to halt a run (D22), so a covered Stop has no
 * fallback at all.
 *
 * ITS WIDTH IS THE READER'S TO SET (D104). The rows print a seed on one line
 * and cut it with an ellipsis, so the pane carries the same seam the thread
 * column does, on its right edge: a drag or the keyboard widens it, a
 * double-click puts it back, and the number rides `nv.prefs` as taste. App
 * hands the committed width in as a prop, clamped by `sidebarPane.ts` against
 * what the thread and the field must keep; while a drag is live the seam
 * paints `SIDEBAR_INSET_VAR` and the thread column follows it in lockstep.
 */

import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type {
  KeyboardEvent as ReactKeyboardEvent,
  PointerEvent as ReactPointerEvent,
  RefObject,
  SVGProps,
} from 'react'
import { forkFamilyLabel, roleModelId, routedEntries, useCanCall, useStore } from '../core/store'
import {
  groupRecent,
  listPinned,
  listRecent,
  pinSession,
  unpinSession,
  type RecentEntry,
  type RecentGroup,
} from '../core/persist'
import { pinnedRows } from '../core/pins'
import { foldedMark, rowMark, type RowMark } from '../core/unread'
import { useRowMarks } from './rowMarks'
import {
  NAME_CHARS,
  explorationName,
  forkLine,
  nameFor,
  nameOrUntitled,
  shownName,
} from '../core/name'
import { forgetExploration, subscribeForgetting } from '../core/forget'
import { SOURCES_DOOR } from '../core/corpus'
import { forgetLine, forgetSentence, useForgetting } from './forgetPress'
import { ROLES, SELECTABLE_MODELS, type ModelEntry, type Role } from '../core/models'
import { PROJECT_LABEL_MAX, projectLabel } from '../core/types'
import { STATUS_WORD, modelCount } from '../core/vocabulary'
import {
  LONG_PRESS_MS,
  LONG_PRESS_SLOP,
  autoScrollStep,
  grabTarget,
  liftAt,
  liftSaid,
  startedDrag,
  stepAt,
  targetAt,
  targetSaid,
  visibleBoxes,
  type DropBox,
  type Grab,
} from './fileDrag'
import {
  MENU_PAD,
  placeAtPoint,
  placeMenu,
  placeSubmenu,
  projectMenuItems,
  rightClickOpensMenu,
  rowMenuItems,
  rowStep,
  stepPressable,
  subStart,
  type MenuItem,
  type ProjectAct,
  type RowAct,
} from './rowMenu'
import { duplicateStored, fileStored, nameStored, openSession } from '../core/autosave'
import { Icon, type IconName } from './components/Icon'
import { useAgo } from './components/ago'
import { DueList } from './DueList'
import { glass } from './components/glass'
import { GlassPane } from './components/GlassShell'
import { useEscapeToClose } from './components/useEscapeToClose'
import { useMedia } from './components/useMedia'
import { PaneSeam } from './components/PaneSeam'
import {
  SIDEBAR_INSET_VAR,
  SIDEBAR_MIN,
  SIDEBAR_SLIDE_VAR,
  SIDEBAR_STEP,
  sidebarBounds,
  stepSidebarWidth,
} from './sidebarPane'

/** The rail's column, collapsed. App reads it — it is `chromeLeft` and
 *  `viewInset.left` over there — so it is exported rather than restated, which
 *  is how two files come to disagree about where the canvas begins. The
 *  expanded column is a number the reader can drag (D104), so it lives with its
 *  bounds in `sidebarPane.ts` and App hands it back in as `width`. */
export const RAIL_W = 52

/**
 * The sidebar's model row: how many of the models the routes name can answer.
 * Three states, never four. The popover's per-row IDLE chip is where a routed
 * refusal is said in full, which is invariant 4's surface rather than this one's.
 * The count through the table's builder (`modelCount`): with one key stored and
 * one route to another company it read "1 models · by role".
 */
export function modelRowText(live: number, routed: boolean, first: string | null): string {
  if (live === 0) return 'no key'
  return routed ? `${modelCount(live)} · by role` : (first ?? 'no model')
}

/** The phone overlay's left margin. On desktop the pane docks flush to the
 *  window (`left: 0`, full height) and takes the whole column, so this margin
 *  applies only to the floating overlay below the breakpoint. Either way the
 *  pane's right edge lands on the width App hands in, or `RAIL_W` — i.e. on
 *  `viewInset.left` — and App leaves the standard 16 of daylight between it and
 *  the session column. */
const PANE_INSET = 8

/**
 * The viewport height under which the pane stops pinning anything.
 *
 * The pane's fixed stack — brand row, four tool rows, footer — is about 300px
 * on a coarse pointer, where every row is 40px. A small phone in landscape
 * (568×320) hands the overlay 304px, so the list, the only flexing box, was cut
 * to nothing behind a shell that clips its overflow, and every stored
 * exploration was unreachable. Under this height the WHOLE COLUMN scrolls as
 * one — brand row, tools, list and footer — and the list stops being a
 * scroller of its own.
 *
 * A style change and never a structural one, and that is the whole of the
 * design: the first cut moved the tools into the scroll region under this
 * height, which unmounted them at the crossing. A control that is focused when
 * the viewport shrinks — browser zoom, a window resized, or a browser whose
 * on-screen keyboard shrinks the layout viewport the moment a field takes
 * focus — lost its focus and its keyboard, and on such a browser tapping the
 * pane's search box (it had one then; D102 moved the search to a sheet) could
 * shrink the viewport, cross the line, and remount the very field being typed
 * into. Nothing moves in the tree now, so focus survives the crossing in both
 * directions. The price is that on a very short screen the tools and the
 * footer scroll away with the rest; 480 leaves the pinned layout to every
 * portrait phone and gives a laptop's 390px landscape twin a comfortable
 * margin above the cliff.
 */
const COMPACT_MAX_H = 480

/** The app's inset-slide timing, the same string the ledger dock and the minimap
 *  ride when a pane moves them. Reduced motion kills it globally (`global.css`). */
const SLIDE = '380ms cubic-bezier(0.22, 1, 0.36, 1)'

/** The routed roles, in a phrase short enough for a popover row. */
const ROLE_WORD: Record<Role, string> = {
  reason: 'derivation · moves',
  check: 'source checks',
  panel: 'the three lenses',
  scan: 'breakthrough scan',
  chat: 'ask',
}

/**
 * The live-run refusal, verbatim from the pill this replaces.
 *
 * It guards both doors that would replace what is on screen: opening another
 * exploration, and — at phone width — expanding a list whose whole job there is
 * opening another exploration. One sentence rather than a fourth near-copy of
 * it; the app already carries three (D22).
 */
/** `index.html`'s own title, given back when nothing is open to name the tab. */
const PAGE_TITLE = 'Pathways — Reasoning Explorer'

const RUN_LIVE_WARN =
  'A run is going. Stop it before opening another exploration — it would stop itself anyway.'

/** One model name, truncating. Shared by the popover's plain rows and its
 *  pressable ones so the two cannot drift — the difference between them must be
 *  the affordance, not the type. */
const NAME_TEXT = {
  display: 'block',
  fontSize: '12px',
  fontWeight: 400,
  color: 'var(--text-1)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const

const EYEBROW = {
  fontSize: 'var(--fs-nano)',
  letterSpacing: '0.16em',
  textTransform: 'uppercase' as const,
  color: 'var(--text-3)',
}

/**
 * A React key for one project, unfiled included.
 *
 * PREFIXED, because a group's key is user-authored text: a project somebody
 * names "unfiled" must not collide with the bucket for the ones nobody has
 * named. One function for the two lists that key on a label — the groups down
 * the pane and the options in the Move-to panel — because the second was written
 * with its own scheme (`label ?? 'none'`) and inherited exactly the collision
 * this prefix exists to prevent, one word over.
 */
function projectKey(project: string | null): string {
  return project === null ? 'unfiled' : `p:${project}`
}

/** What a row is called: the name somebody gave it, else its seed clamped like
 *  a project label, else the standing name for a seed with no words in it
 *  (D105). Every surface in this file that names a row says it through here, so
 *  a row is named the same wherever it is named. */
function nameOf(e: { seed: string; name?: string }): string {
  return nameOrUntitled(e)
}

/** Up to two initials, or nothing at all. An avatar that invents a letter from
 *  an empty name is a person this app has not met. */
function initialsOf(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      // By CODE POINT, not by `w[0]`: that is a UTF-16 code unit, so a name
      // beginning past the basic plane hands back half a surrogate pair and the
      // orb renders the replacement glyph instead of the letter it was given.
      .map((w) => [...w][0]!.toUpperCase())
      .join('')
  )
}

/** Where a right-click landed, in viewport pixels — where its menu opens. */
type Point = { x: number; y: number }

/** Whether the primary pointer is fine: what a right-click with no pointer type
 *  is judged by (`rightClickOpensMenu`). */
const finePointer = () =>
  typeof window.matchMedia === 'function' && window.matchMedia('(pointer: fine)').matches

/** One row on its way somewhere: what is moving, and where it started. */
interface Moving {
  id: string
  seed: string
  /** The row's given name, when it has one, so the ghost says what the row says. */
  name?: string
  from: string | null
}

/** …and where the pointer has it now. `over` is the group a drop would land in,
 *  null for none — which is a cancel, and is not the unfiled bucket. */
type DragState = Moving & { x: number; y: number; over: DropBox | null }

export function ExplorationsSidebar({
  width,
  stageW,
  reservedRight,
}: {
  /** The expanded column's width of record, clamped by App for this window. */
  width: number
  /** The stage's own width and what the thread keeps of it, for the seam's
   *  bounds — the same numbers App clamps the width with, so the handle can
   *  never offer a width the pane would not be given. */
  stageW: number
  reservedRight: number
}) {
  const sessionId = useStore((s) => s.session.id)
  const collapsed = useStore((s) => s.sidebarCollapsed)
  const setSidebarCollapsed = useStore((s) => s.setSidebarCollapsed)
  const setSidebarWidth = useStore((s) => s.setSidebarWidth)
  /** The width at which every name on screen reads in full: measured off the
   *  rows below after each render and published for App's clamp, so the seam's
   *  ceiling is just past the longest name (D104, follow-up). */
  const sidebarFit = useStore((s) => s.sidebarFit)
  const setSidebarFit = useStore((s) => s.setSidebarFit)
  const profileName = useStore((s) => s.profileName)
  const setProfileName = useStore((s) => s.setProfileName)
  const savedAt = useStore((s) => s.savedAt)
  const modelId = useStore((s) => s.modelId)
  const roleModels = useStore((s) => s.roleModels)
  const keys = useStore((s) => s.keys)
  const customBase = useStore((s) => s.customBase)
  const setSetup = useStore((s) => s.setSetup)
  const researchOpen = useStore((s) => s.researchOpen)
  const setResearchOpen = useStore((s) => s.setResearchOpen)
  const setModel = useStore((s) => s.setModel)
  const setExport = useStore((s) => s.setExport)
  /** The two shelves the tools column opens onto besides Research Lab: the
   *  documents a source check may search, and everything saved on this device. */
  const setSources = useStore((s) => s.setSources)
  const setLibrary = useStore((s) => s.setLibrary)
  /** The search, which is a sheet and not a box in this column (D102). */
  const setSearchOpen = useStore((s) => s.setSearchOpen)
  const newExploration = useStore((s) => s.newExploration)
  const duplicateSession = useStore((s) => s.duplicateSession)
  const setProject = useStore((s) => s.setProject)
  const setName = useStore((s) => s.setName)
  /** Projects made and not yet filled. Every other group is derived from the
   *  labels on the stored rows; `core/projects.ts` says why these need a slot of
   *  their own and what keeps them from becoming a second source of truth. */
  const declaredProjects = useStore((s) => s.declaredProjects)
  const declareProject = useStore((s) => s.declareProject)
  const undeclareProject = useStore((s) => s.undeclareProject)
  const syncProjects = useStore((s) => s.syncProjects)
  /** Projects folded away under their headings, and the one door that folds or
   *  unfolds one — the folder on the heading (D101). */
  const foldedProjects = useStore((s) => s.foldedProjects)
  const toggleProjectFold = useStore((s) => s.toggleProjectFold)
  /** When each exploration was last opened here, and the act that stamps one.
   *  The dot beside a row fills while its latest run's finish, or an answer
   *  that landed away, is newer than that, and pressing the row — open already
   *  or not — is an opening (D103, D334). */
  const openedAt = useStore((s) => s.openedAt)
  const markOpened = useStore((s) => s.markOpened)
  /** What is arriving where, and where an answer landed unseen — the live half
   *  of a row's mark, composed once for this pane and the search sheet. */
  const liveMarks = useRowMarks()
  const markOf = (e: RecentEntry) => rowMark(e, openedAt, liveMarks)
  /** The OPEN exploration's label, off the live session rather than off its
   *  index row. A scalar selector, so this is a signal like `savedAt` and not a
   *  subscription to the session — it changes only when somebody files. */
  const openProject = useStore((s) => s.session.project)
  /** The OPEN exploration's name and where it was forked from, read the same way
   *  and for the same reason: a Rename of the open row lands in the store first,
   *  and a fork's row is written by the id-change checkpoint a beat after the
   *  canvas switched to it. Scalars and a record carried by reference, so these
   *  change only when somebody names or forks. */
  const openName = useStore((s) => s.session.name)
  const openForked = useStore((s) => s.session.forkedFrom)
  const openSeed = useStore((s) => s.session.seed)
  /** The OPEN exploration's latest run's end, off the live session — the index's
   *  own "last run" (`summarise`) — for the same reason again, and a sharper one:
   *  a run's end lands in the store at once, and the index only when the autosave
   *  flushes, up to a second later. Read off the index alone, the open row's ring
   *  went out the moment the run ended and its dot came a beat after, under the
   *  run's own toast: ring, nothing, dot, where the ring should fill (D334). */
  const openFinishedRunAt = useStore((s) => s.session.runs.at(-1)?.finishedAt)
  /* THE TAB'S TITLE is the open row's name, said the way the row says it — so
     two tabs on two forks read as two things in the browser's own strip, and a
     Rename shows there too. Here, beside the rows, because this pane is always
     mounted and is where a row's name is decided; an empty canvas has no name
     and gives the page's own title back. */
  useEffect(() => {
    const name = shownName({ seed: openSeed, name: openName })
    document.title = name ? `${name} — Pathways` : PAGE_TITLE
  }, [openSeed, openName])
  const toast = useStore((s) => s.toast)
  const runLive = useStore((s) => s.run !== null && s.run.phase !== 'ended')
  /** The pass lock. Read beside `runLive` because the fork door needs BOTH arms
   *  of the lock `duplicateSession` holds — a single pass in flight sets this
   *  and never touches `run`. */
  const busy = useStore((s) => s.busy)
  const ready = useCanCall()
  const narrow = useMedia('(max-width: 767px)')
  /** Too short to pin anything. Below this the whole column scrolls as one and
   *  the list is not a scroller of its own — see `COMPACT_MAX_H` and `scroller`. */
  const compact = useMedia(`(max-height: ${COMPACT_MAX_H}px)`)

  /** The phone's overlay is LOCAL and always starts shut. `sidebarCollapsed` is
   *  a persisted desktop layout preference; letting a transient overlay write it
   *  would have a phone session decide how the next desktop one opens. */
  const [phoneOpen, setPhoneOpen] = useState(false)
  /** The row whose Forget is armed. One at a time — and in one PLACE at a
   *  time: a pinned row is drawn twice, and the confirm replaces the copy that
   *  was pressed rather than both, which would be two Forget buttons for one
   *  question. */
  const [confirming, setConfirming] = useState<{ id: string; pinned: boolean } | null>(null)
  /** The row whose name field is open, and which copy — the confirm's rule: the
   *  field stands in for the copy that was pressed, never for both. */
  const [renaming, setRenaming] = useState<{ id: string; pinned: boolean } | null>(null)
  /** The row whose ⋮ menu is up, and which copy of it. The ⋮ itself is in
   *  `menuAnchor`: where the menu hangs, and where focus goes back to. `at` is
   *  the pointer a right-click opened it at, where it opens instead (D368). */
  const [menu, setMenu] = useState<{ id: string; pinned: boolean; at?: Point } | null>(null)
  /** The project heading whose menu is up (D368) — the row menu's twin, and one
   *  of the same exclusive layers. Its anchor rides `menuAnchor` too: one menu
   *  is up at a time, so one ref says where focus goes back. */
  const [projectMenu, setProjectMenu] = useState<{ project: string; at?: Point } | null>(null)
  const menuAnchor = useRef<HTMLElement | null>(null)
  /** The project whose heading is a name field right now (D368). */
  const [renamingProject, setRenamingProject] = useState<string | null>(null)
  /** The name field under the PROJECTS eyebrow's +, and what is in it. */
  const [naming, setNaming] = useState(false)
  const [projectDraft, setProjectDraft] = useState('')
  /** A group heading to light briefly and scroll to — what “that one already
   *  exists” looks like, and what a new one does on arrival. */
  const [flash, setFlash] = useState<string | null>(null)
  /** A row under the pointer, on its way somewhere. Mirrored from `dragRef`,
   *  which is what the pointer handlers actually read — see the move block. */
  const [drag, setDrag] = useState<DragState | null>(null)
  /** A row lifted by the keyboard, and where its target stands. */
  const [grab, setGrab] = useState<Grab | null>(null)
  /** What a reader is told about all of it. Polite, so it never interrupts. */
  const [say, setSay] = useState('')
  /** Bumped by the acts in this file that write the index. `duplicateStored`
   *  writes documents without going through the autosave, so `savedAt` alone
   *  would not tell the list its own fork had landed. */
  const [wrote, setWrote] = useState(0)
  const [modelsOpen, setModelsOpen] = useState(false)
  const [editingName, setEditingName] = useState(false)
  const [nameDraft, setNameDraft] = useState('')

  const modelsId = useId()
  const menuId = useId()
  const profileRef = useRef<HTMLButtonElement | null>(null)
  const nameRef = useRef<HTMLInputElement | null>(null)
  const modelsRowRef = useRef<HTMLButtonElement | null>(null)
  const radioRef = useRef<HTMLButtonElement | null>(null)
  const projectRef = useRef<HTMLInputElement | null>(null)
  /** The move, ended. Held in a ref so `showLayer` can take a row in motion down
   *  without this file having to declare the two in dependency order. */
  const endMoveRef = useRef<(() => void) | null>(null)
  const projectBtnRef = useRef<HTMLButtonElement | null>(null)
  /** The scrolling list. Measured while a drag is live — for the groups' boxes
   *  and for its own edges, which is what the auto-scroll is against. */
  const listRef = useRef<HTMLDivElement | null>(null)
  const flashTimer = useRef(0)
  /** Which ✕ is owed the focus after one of them removed its own group, or null
   *  for the button that makes them. `undefined` is "nobody is owed anything". */
  const wantAfterRemove = useRef<string | null | undefined>(undefined)
  /* The move, in refs rather than state, because a pointer gesture is answered
     between renders: the handlers below are attached at pointerdown and have to
     read what is true NOW, not what was true when they were made. `drag` and
     `grab` above are the render's copy of the first two. */
  const pressRef = useRef<(Moving & { x: number; y: number; coarse: boolean }) | null>(null)
  const dragRef = useRef<DragState | null>(null)
  const grabRef = useRef<Grab | null>(null)
  /** Set the moment a press crosses the threshold, cleared by the next press. A
   *  press that became a drag must not also open the exploration on the way up. */
  const draggedRef = useRef(false)
  const detachRef = useRef<(() => void) | null>(null)
  const holdTimer = useRef(0)
  const frame = useRef(0)
  /** The bounded clear of `draggedRef` — see `endMove`. */
  const clearDragged = useRef(0)
  /** The groups' boxes, measured on the events that move them rather than on
   *  every pointer sample. See `remeasure`. */
  const boxesRef = useRef<DropBox[]>([])
  const paneRef = useRef<HTMLDivElement | null>(null)
  const openerRef = useRef<HTMLButtonElement | null>(null)
  /** A control the rail does not have asks to be focused once the pane holding
   *  it exists. The element is mounted one commit later, so the request has to
   *  survive that commit — a straight `.focus()` here is a silent no-op that
   *  strands focus on `<body>` (D22 records the same finding on the pill). */
  const wantFocus = useRef<'profile' | 'current' | null>(null)
  /** Escape out of the name field must not commit what the blur is about to. */
  const cancelName = useRef(false)
  const wantProfileRow = useRef(false)

  const open = narrow ? phoneOpen : !collapsed
  const rail = !narrow && collapsed

  /* THE NAMES' OWN CEILING. After every render of the expanded column, the
     width the pane would need for its longest name to read in full: for each
     name, the pane's width less the name's box is the room everything beside
     the name takes, plus the name's own laid-out width — a Range over the
     text, which measures the text and not the box that clips it — plus one
     pixel so the ellipsis is not asked for at exactly the fit. The pane and
     the box are read in one frame, so a width in mid-transition measures the
     same as one at rest. Published only when it changes, so the render it
     causes measures the same and stops. Not the rail, which has no names,
     and not the phone's overlay, which is a fixed width. */
  const [, askMeasure] = useState(0)
  useEffect(() => {
    // A name measured in a fallback face is a name measured wrong; once the
    // faces are in, measure again. And measure again when the shell's width
    // stops moving: the measure below sits out a transition, so the one after
    // the rail opens has to be asked for.
    void document.fonts?.ready.then(() => askMeasure((n) => n + 1))
    const shell = paneRef.current?.closest('aside')
    if (!shell) return
    const settled = (e: TransitionEvent) => {
      if (e.propertyName === 'width') askMeasure((n) => n + 1)
    }
    shell.addEventListener('transitionend', settled)
    return () => shell.removeEventListener('transitionend', settled)
  }, [])
  useLayoutEffect(() => {
    if (narrow || rail) return
    const pane = paneRef.current
    if (!pane) return
    // The SHELL's width, not this column's: App sizes the shell, and the column
    // sits inside the shell's own border, so a fit measured off the column
    // would come out short by exactly that border and the longest name would
    // lose its last pixel at the seam's own ceiling.
    const paneW = (pane.closest('aside') ?? pane).getBoundingClientRect().width
    // Only at rest. While the rail is opening the shell is narrower than the
    // width App committed and the name boxes are crushed to nothing, so the
    // pane less a box is not the room beside the name — a measure taken then
    // came out short, and the pane it clamped wobbled up to its width over
    // three transitions. `transitionend` above asks again once it is still.
    if (Math.abs(paneW - width) > 0.5) return
    let need = SIDEBAR_MIN
    for (const el of pane.querySelectorAll<HTMLElement>('.explrow__name')) {
      const range = document.createRange()
      range.selectNodeContents(el)
      const text = range.getBoundingClientRect().width
      need = Math.max(need, Math.ceil(paneW - el.getBoundingClientRect().width + text) + 1)
    }
    if (need !== sidebarFit) setSidebarFit(need)
  })
  /** The box that scrolls the rows: the list itself, or under `COMPACT_MAX_H`
   *  the whole column. Every hand the drag lends to scrolling — the clip for the
   *  groups' boxes, the auto-scroll, the scroll-cancels-a-hold listener — asks
   *  here rather than assuming the list, so a short screen is not a screen where
   *  a drag scrolls the wrong box. */
  const scroller = () => (compact ? paneRef.current : listRef.current)
  /** A row is in motion, by either hand. One flag, because every layer that has
   *  to yield to it — Escape, the phone overlay's dismissal — yields to both. */
  const moving = drag !== null || grab !== null

  /* ── Doors ─────────────────────────────────────────────────────────────── */

  const expand = useCallback(
    (want: 'profile' | 'current' | null = null) => {
      if (narrow) {
        // At this width the pane is an overlay over the corner the run pod owns,
        // and the pod's Stop and "Not there" are the two controls that halt or
        // steer something costing money while it is costing it. Escape may not
        // stop a run (D22), so a covered Stop has no fallback — the list waits.
        if (runLive) {
          toast(RUN_LIVE_WARN, 'warn')
          return
        }
        wantFocus.current = want
        setPhoneOpen(true)
        return
      }
      wantFocus.current = want
      setSidebarCollapsed(false)
    },
    [narrow, runLive, setSidebarCollapsed, toast],
  )

  const shut = useCallback(() => {
    if (narrow) {
      setPhoneOpen(false)
      openerRef.current?.focus()
      return
    }
    setSidebarCollapsed(true)
  }, [narrow, setSidebarCollapsed])

  /**
   * Show ONE of the pane's transient layers, and shut the rest. `null` shuts
   * them all.
   *
   * They are mutually exclusive, and this is the one place that says so. They
   * were not: each opener set its own flag, and the away-pointerdown that
   * dismissed the models popover only fires on a POINTER — so opening a second
   * layer with the keyboard left the popover up underneath it. Escape then hit
   * both, because `useEscapeToClose` and its twin are two window-capture
   * listeners on the same target and `stopPropagation` does not stop a
   * same-phase listener beside it: two layers peeled on one press, then two
   * focus restorations fighting over where to land. The hook's contract is one
   * press, one layer (see components/useEscapeToClose.ts), and that is only
   * true if one layer is up.
   *
   * A ROW IN MOTION IS ONE OF THEM. A drag or a keyboard lift is a question in
   * progress with an Escape of its own, so opening a popover over the top of one
   * would leave two things listening for the same key. It is cleared here rather
   * than at each opener for the same reason the other two are.
   *
   * Openers come through here; each layer keeps its own CLOSER, because closing
   * is where focus has to be handed back to the control that opened it and only
   * that layer knows which one that is.
   *
   * The profile name field and the new-project field are deliberately not on
   * this list. Both commit or cancel on blur, so reaching any of these layers —
   * by pointer or by Tab — already takes them down; adding them here would be a
   * second mechanism for something that cannot be in two places anyway.
   */
  const showLayer = useCallback(
    (
      l:
        | { kind: 'models' }
        | { kind: 'forget'; id: string; pinned: boolean }
        | { kind: 'rename'; id: string; pinned: boolean }
        | { kind: 'menu'; id: string; pinned: boolean; anchor: HTMLElement; at?: Point }
        | { kind: 'projectMenu'; project: string; anchor: HTMLElement; at?: Point }
        | { kind: 'renameProject'; project: string }
        | null,
    ) => {
      setModelsOpen(l?.kind === 'models')
      setConfirming(l?.kind === 'forget' ? { id: l.id, pinned: l.pinned } : null)
      setRenaming(l?.kind === 'rename' ? { id: l.id, pinned: l.pinned } : null)
      menuAnchor.current = l?.kind === 'menu' || l?.kind === 'projectMenu' ? l.anchor : null
      setMenu(l?.kind === 'menu' ? { id: l.id, pinned: l.pinned, at: l.at } : null)
      setProjectMenu(l?.kind === 'projectMenu' ? { project: l.project, at: l.at } : null)
      setRenamingProject(l?.kind === 'renameProject' ? l.project : null)
      endMoveRef.current?.()
    },
    [],
  )

  // The phone overlay's two dismissals. Escape on the capture phase so the press
  // stops here rather than deselecting a node under a panel somebody was trying
  // to close; a pointerdown outside instead of a backdrop, because a fixed
  // inset:0 scrim at this width covers every Stop in the app (D22).
  //
  // Written out rather than taken from `useEscapeToClose` for the one thing that
  // hook cannot know: the layers INSIDE this pane answer Escape first. Two
  // window-capture listeners both fire on the same press — `stopPropagation` is
  // not `stopImmediatePropagation` — so the hook would take the pane down
  // together with the popover or the field the press was actually for.
  useEffect(() => {
    if (!narrow || !phoneOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      // …and a row in motion is one of those layers: a drag or a lift ends on
      // Escape through `useEscapeToClose`, and without this the same press would
      // take the whole pane down with it. So is an armed Forget, whose Escape is
      // Keep (D335) — answered in React's capture phase, which a window-capture
      // listener runs before, so this one took the pane down with the confirm
      // still armed under it.
      if (
        modelsOpen ||
        moving ||
        menu !== null ||
        projectMenu !== null ||
        confirming !== null ||
        e.target instanceof HTMLInputElement
      )
        return
      e.stopPropagation()
      shut()
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [narrow, phoneOpen, modelsOpen, moving, menu, projectMenu, confirming, shut])
  useEffect(() => {
    if (!narrow || !phoneOpen) return
    const away = (e: PointerEvent) => {
      const t = e.target as Node
      if (paneRef.current?.contains(t) || openerRef.current?.contains(t)) return
      // A row's menu is the pane's, but it is not IN the pane: it rides the
      // ghost's portal to the body (see `RowMenu`), so a press on one of its
      // items lands outside this box — and dismissed the overlay from under
      // the menu on every tap, on a phone, where every press is a tap.
      if (document.getElementById(menuId)?.contains(t)) return
      setPhoneOpen(false)
    }
    window.addEventListener('pointerdown', away)
    return () => window.removeEventListener('pointerdown', away)
  }, [narrow, phoneOpen, menuId])

  // A rail control that asks for a control the rail does not have, once it
  // exists. It lands on that control, never on the pane.
  useEffect(() => {
    if (!open) return
    const want = wantFocus.current
    if (!want) return
    wantFocus.current = null
    if (want === 'current') focusCurrentRow()
    else profileRef.current?.focus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  /** The row of the exploration the thread is reading — its open press, the
   *  one `aria-current` names — or the list's first where it has none. */
  const focusCurrentRow = () => {
    const pane = paneRef.current
    const row =
      pane?.querySelector<HTMLElement>('.explrow__open[aria-current]') ??
      pane?.querySelector<HTMLElement>('.explrow__open')
    row?.focus()
    row?.scrollIntoView({ block: 'nearest' })
  }

  // The thread's breadcrumb (D362): "Explorations" is the level above the title
  // it leads to, so its press is this pane's door — open it where it is shut, and
  // land on the row of the exploration being read. On the CHANGE, as the store's
  // other one-shots are read, so a remount never replays an old press.
  const sigExplorations = useStore((s) => s.sigExplorations)
  const seenExplorations = useRef(sigExplorations)
  useEffect(() => {
    if (sigExplorations === seenExplorations.current) return
    seenExplorations.current = sigExplorations
    if (open) focusCurrentRow()
    else expand('current')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sigExplorations])

  // Nothing stays armed across a close. Escape, a press away and the collapse
  // button all leave without touching these, and each used to bring the pane
  // back on a destructive confirm or a radiogroup nobody asked for.
  useEffect(() => {
    if (open) return
    showLayer(null)
    setNaming(false)
    setEditingName(false)
  }, [open, showLayer])

  const bump = () => setWrote((n) => n + 1)
  // A Forget pressed outside this pane — the composer giving back a refused lone
  // seed (D193), the library, the palette — removes its index row only after the
  // shelf commits the delete (D331). By then the session-id change the press made
  // has already re-read the list with the row still in it, a blank canvas never
  // autosaves, and nothing else told the list: the refused question stayed in
  // Recent as a row that opened onto "no longer stored" (D342). The registry
  // announces after `forgetSession`'s bookkeeping, so the re-read sees the row
  // gone, and a refused delete keeps it, as D331 says it must.
  useEffect(() => subscribeForgetting(() => setWrote((n) => n + 1)), [])

  /** Read at a signal — see the header. `sessionId` is in the list because the
   *  open row wears a marker and a fork changes which row that is. */
  const recents: RecentEntry[] = useMemo(
    () =>
      // The OPEN exploration's project is read off the live session, not off its
      // index row. The autosave trails the store by up to a second, so a row
      // filed from here would sit under its old heading until that save landed
      // — the toast would say one thing and the list another. Same fact the fork
      // door already states in its own words: for the open exploration the live
      // document is ahead of the index row.
      listRecent().map((e) =>
        e.id === sessionId
          ? {
              ...e,
              project: openProject,
              name: openName,
              forkedFrom: openForked,
              ...(openFinishedRunAt !== undefined ? { finishedRunAt: openFinishedRunAt } : {}),
            }
          : e,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [savedAt, wrote, sessionId, openProject, openName, openForked, openFinishedRunAt],
  )
  /** The pinned ids, read at the same signal as the rows they point at and for
   *  the same reason — the pin act writes without the autosave and bumps
   *  `wrote` the way a fork does. Never pruned here: `core/pins.ts` says why a
   *  page must not reconcile pins against the rows it happens to see. */
  const pinnedIds = useMemo(
    () => listPinned(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [savedAt, wrote],
  )
  const pinnedSet = useMemo(() => new Set(pinnedIds), [pinnedIds])
  /** Both sections read from one index: the whole of it, always. Nothing in
   *  this pane filters any more — the search is a sheet that lists what matches
   *  (D102) — so a row is on screen here because it is stored, and a pinned row
   *  is drawn again because it is pinned. */
  const groups = useMemo(() => groupRecent(recents), [recents])
  /** The one mark the list wears while it is folded — the collapsed rail and
   *  the phone's opener show no rows, so a run finishing, or an answer landing,
   *  would otherwise be invisible for exactly as long as the list is away,
   *  which on a phone is most of the time (D334). */
  const folded = foldedMark(recents, openedAt, liveMarks)
  /** The names two or more stored rows share, over the whole index rather than
   *  one section: a twin filed under a project is still a twin of the Recent row
   *  it forked from, and that is the pair somebody is trying to tell apart. */
  const twins = useMemo(() => {
    const seen = new Map<string, number>()
    for (const e of recents) seen.set(nameOf(e), (seen.get(nameOf(e)) ?? 0) + 1)
    return new Set([...seen].filter(([, n]) => n > 1).map(([name]) => name))
  }, [recents])
  const pinnedShown = useMemo(() => pinnedRows(pinnedIds, recents), [pinnedIds, recents])
  /**
   * Every label a LIVE row carries — which is to say, every group this pane
   * draws by derivation.
   *
   * The registry beside it is what lets a label exist with nothing under it: a
   * memberless label can be declared, draws as an empty group, and things can be
   * dropped into it.
   */
  const inUse = useMemo(() => {
    const set = new Set<string>()
    for (const e of recents) {
      const label = projectLabel(e.project)
      if (label) set.add(label)
    }
    return set
  }, [recents])

  /* A declared project leaves the registry the moment a member arrives, and from
     then on its group is derived like every other — `core/projects.ts` holds the
     rule and the reason. HERE because this is where both shelves have just been
     read, and it is the one caller: a rule implemented at each of the doors that
     can file something is the shape this codebase keeps being bitten by. Silent
     when nothing moved, so it cannot loop against its own render. */
  useEffect(() => {
    syncProjects(inUse)
  }, [inUse, syncProjects])

  /** Projects made and still empty — every one of them, like the rows. */
  const emptyShown = useMemo(
    () => declaredProjects.filter((p) => !inUse.has(p)),
    [declaredProjects, inUse],
  )

  /**
   * The groups the pane draws, empty ones first.
   *
   * FIRST, and only until they have a member. An empty project is a place
   * somebody just made and is about to drag into, so it earns the top; the
   * moment something lands in it, it leaves the registry and derivation starts
   * sorting it by recency like every other group. That is the whole of the
   * ordering rule, and it is why there is no second sort to keep in step.
   */
  const shown = useMemo(
    () => [...emptyShown.map((project) => ({ project, entries: [] as RecentEntry[] })), ...groups],
    [emptyShown, groups],
  )

  /**
   * Every group this pane would DRAW — what "is that one already here?" is
   * asked against.
   *
   * RENDERED groups rather than every label the device has ever seen: a
   * label with nothing under it draws nothing, so answering "already here" about
   * it would point at a heading that does not exist. Declaring it is legal and is
   * the way back to it — see `inUse`.
   */
  const allLabels = useMemo(
    () => [...new Set([...declaredProjects, ...inUse])],
    [declaredProjects, inUse],
  )

  /** Where a move can put a row: every group ON SCREEN, in the order they are
   *  drawn. The same set both hands reach, so a drag and a keyboard lift can do
   *  exactly the same things — and only what is on screen, because a target
   *  nobody can see is one nobody chose. */
  const candidates = useMemo(() => shown.map((g) => g.project), [shown])

  /** The named groups and the unfiled bucket, drawn under their own eyebrows.
   *  `shown` keeps them in one order for the drag's `candidates`; the render
   *  splits them because the two sections are not alike — PROJECTS draws
   *  always, for the + on its eyebrow, and RECENT only over rows (D102). */
  const named = useMemo(
    () => shown.filter((g): g is RecentGroup & { project: string } => g.project !== null),
    [shown],
  )
  const unfiled = useMemo(() => shown.find((g) => g.project === null) ?? null, [shown])

  /**
   * Whether a group's rows are folded away right now.
   *
   * Nothing here unfolds a project behind somebody's back. D101's search
   * unfolded what it matched, because a match hidden under a shut folder was a
   * search that lied; the search is a sheet of its own now and lists its
   * matches itself (D102), so the fold is exactly what somebody chose, until
   * they choose otherwise.
   */
  const foldedSet = useMemo(() => new Set(foldedProjects), [foldedProjects])
  const isFolded = (project: string | null) => project !== null && foldedSet.has(project)

  /** The group a row is drawn in — the derivation `groupRecent` makes, restated
   *  for a pinned row, which is drawn away from its group and still has to say
   *  where a lift starts from and what "left where it was" means. */
  const groupOf = (e: RecentEntry) => projectLabel(e.project) ?? null

  /* THE PALETTE'S RENAME (`sigNaming`). The palette has no field and must not
     grow one, so it asks this pane to open the name field on the OPEN row: the
     pane opens if it is shut, the pinned copy takes the field when there is one
     (it is always on screen), and a folded project unfolds — the reader asked to
     see this row's name, which is the one case the fold rule above gives way
     to. A phone with a run going refuses in `expand`'s own words, before
     anything is armed. The counter's resting value is not a request. */
  const sigNaming = useStore((s) => s.sigNaming)
  const namingSeen = useRef(sigNaming)
  useEffect(() => {
    if (sigNaming === namingSeen.current) return
    namingSeen.current = sigNaming
    const row = recents.find((e) => e.id === sessionId)
    if (!row) {
      toast('This exploration is listed once it grows past its seed — name it then.', 'info')
      return
    }
    if (narrow && runLive) {
      toast(RUN_LIVE_WARN, 'warn')
      return
    }
    if (!open) expand()
    const pinned = pinnedSet.has(row.id)
    const group = groupOf(row)
    if (!pinned && group !== null && isFolded(group)) toggleProjectFold(group)
    showLayer({ kind: 'rename', id: row.id, pinned })
    // Only the signal: everything else is read as it stands when it fires.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sigNaming])

  const openRow = (e: RecentEntry) => {
    // A press that became a drag is not a press. The flag is set the moment the
    // pointer crosses the threshold and cleared by the next press, so a click
    // arriving after a drop opens nothing — and a row held by the keyboard
    // answers Enter with a drop, which is handled before this is ever reached.
    if (draggedRef.current) {
      draggedRef.current = false
      return
    }
    if (grabRef.current) return
    if (runLive) {
      toast(RUN_LIVE_WARN, 'warn')
      return
    }
    // An opening, whichever row this is — the open exploration's own included,
    // which is how its filled dot is put out without leaving it. A switch is
    // stamped again by the autosave's hook, a moment later; harmless.
    markOpened(e.id)
    if (narrow) setPhoneOpen(false)
    if (e.id !== sessionId) openSession(e.id)
  }

  /**
   * Share or export one row.
   *
   * The OPEN exploration goes by its live document, not its id: the autosave
   * trails the store by up to eight seconds, and a row that read the disk copy
   * could share a graph missing the newest nodes. Stored rows keep the id path —
   * that is what lets them export without opening.
   *
   * Nothing here closes the pane, and that is the focus discipline rather than
   * laziness: `useModalFocus` captures `document.activeElement` in an effect that
   * runs after the sheet renders, so a surface that unmounted the pressed button
   * first would leave the restore target as `<body>` and strand the user at the
   * top of the document when the sheet closes.
   */
  const share = (e: RecentEntry) => setExport(true, e.id === sessionId ? null : e.id)

  /**
   * Pin one exploration to the head of the list, or take the pin down.
   *
   * One press, one state, no panel — which is what separates this from the
   * fourth act D56 refused. Filing was a choice among every project on the
   * device, and a glyph opening that choice described a movement the reader
   * could make directly; a pin has no movement that means it, and the press IS
   * the whole act. The slot is written synchronously (`pinSession`), the list
   * re-read through `wrote` like every other act here that writes without the
   * autosave, and the sentence said twice for the reason `fileRow` gives: the
   * row moves to a section a reader cannot see it arrive in.
   */
  const togglePin = (e: RecentEntry) => {
    const on = pinnedSet.has(e.id)
    if (!(on ? unpinSession(e.id) : pinSession(e.id))) {
      const said = on
        ? 'That pin could not be taken down.'
        : 'That exploration could not be pinned.'
      toast(said, 'bad')
      setSay(said)
      return
    }
    bump()
    const said = on ? 'Unpinned.' : 'Pinned.'
    toast(said, 'good')
    setSay(said)
  }

  /**
   * Copy one exploration, and switch to the copy.
   *
   * Two doors because the two acts differ at the storage boundary: the open one
   * goes through the store, which holds the same lock `newExploration` does and
   * says so itself — a second sentence here would refuse twice. A stored one is
   * copied on disk and then opened, so nothing downstream holds that lock for
   * the pair; this file holds it, with the store's own predicate and the store's
   * own sentence.
   *
   * The list is re-read either way, and not only for the new row: a fork of an
   * UNFILED source files the source too, under the family's new name, so the row
   * that was pressed moves group as well.
   */
  const fork = (e: RecentEntry) => {
    let label: string | undefined
    if (e.id === sessionId) {
      if (!duplicateSession()) return
      // The store's copy IS the session now, so its family is read back off it:
      // for the open exploration the live document is ahead of the index row.
      label = useStore.getState().session.project
    } else {
      // The SAME lock `duplicateSession` holds one door over, in its own words,
      // because this door is two acts and each was guarded by somebody else. A
      // pass in flight is not a live run, so it walked past a `runLive` test,
      // `duplicateStored` wrote the copy, `openSession` then refused the switch
      // on its own busy guard — and the fork was announced anyway, naming the
      // group of the exploration still on screen. One predicate, before
      // anything is written.
      if (busy || runLive) {
        toast('One thought at a time — let the current branch finish.', 'warn')
        return
      }
      const copy = duplicateStored(e.id)
      if (!copy) {
        toast('That exploration could not be copied.', 'bad')
        return
      }
      // Named from the SOURCE ROW rather than from the store after the switch:
      // `openSession` can still refuse, and the session read back then belongs
      // to whatever is on the canvas, not to the copy. Both doors name a family
      // through `forkFamilyLabel`, so the row's own project — or the family its
      // seed is about to mint — is the group the copy lands in.
      label = e.project ?? forkFamilyLabel(e.seed)
      openSession(copy)
    }
    bump()
    if (narrow) setPhoneOpen(false)
    toast(label ? `Forked into “${label}”.` : 'Forked.', 'good')
  }

  /**
   * Keep what the name field holds, for one row — or take the name away, when
   * the field was emptied or holds the seed's own words (`nameFor`).
   *
   * `fileRow`'s two writers, for `fileRow`'s reason: the OPEN exploration is
   * named through the store, where it lives and where ⌘Z can take the name back;
   * a stored one through `nameStored`, which refuses the open id itself. Only the
   * name moves — the seed each row is reasoned from is not in either write.
   *
   * Focus goes back to the row's ⋮ once the field has gone, through the same
   * owed-focus signal a filing uses — and only if it fell: a blur that kept the
   * name was a press somewhere else, and that press keeps its focus.
   */
  const rename = (e: RecentEntry, pinned: boolean, typed: string) => {
    showLayer(null)
    wantMore.current = { id: e.id, pinned }
    const was = nameFor(e.name, e.seed)
    const next = nameFor(typed, e.seed)
    if (next === was) {
      bump()
      setSay('Name left as it was.')
      return
    }
    if (e.id === sessionId) setName(next ?? null)
    else if (!nameStored(e.id, next ?? null)) {
      bump()
      toast('That exploration could not be renamed.', 'bad')
      setSay('That exploration could not be renamed.')
      return
    }
    bump()
    const said = next
      ? `Named “${next}”.`
      : `Name cleared — it reads “${explorationName(e.seed) || 'Untitled exploration'}” again.`
    toast(said, 'good')
    setSay(said)
  }

  /** The name field, left as it was: Escape. Focus goes back to the ⋮. */
  const unname = (e: RecentEntry, pinned: boolean) => {
    showLayer(null)
    wantMore.current = { id: e.id, pinned }
    bump()
  }

  /**
   * A new exploration IN a project — the + on its heading.
   *
   * The shared door, handed the label: the lock, its sentence and the clear are
   * the ones every other way to a fresh canvas has, and what this adds is only
   * the filing, which the store writes onto the fresh session itself and carries
   * into the seed (`newExploration`, `startSession`). Nothing appears in the
   * list yet — an empty canvas is never written to disk — so the toast is what
   * says where the seed will land, and the live region says it for whoever
   * cannot see the toast.
   */
  const startIn = (label: string) => {
    if (!newExploration(label)) return
    if (narrow) setPhoneOpen(false)
    const said = `New exploration in “${label}” — the seed you type lands there.`
    toast(said, 'good')
    setSay(said)
  }

  /* ── Filing by hand ────────────────────────────────────────────────────── */

  /**
   * File one exploration under a project, or take its label away.
   *
   * The one door under both hands — a row dropped into a group and a row
   * dropped there by the keyboard land here, so the two gestures cannot come to
   * mean different things.
   *
   * TWO WRITERS BEHIND IT, and the branch is a TRAP rather than a tidiness.
   * `fileStored` writes the document on disk; the OPEN exploration is not on
   * disk in any sense this could reach — it is held in memory and written by the
   * autosave on session object identity, up to a second later and again on
   * `pagehide`. A label written straight to its document is therefore a label
   * the next debounce overwrites with the in-memory session that never carried
   * one: it would appear, the row would regroup, and a second later it would be
   * gone with nothing on screen saying so. So the open row goes through the
   * store's own writer, which is where that session lives — and `fileStored`
   * refuses the open id itself as well, so the trap is shut at both ends rather
   * than only described here.
   *
   * `setProject` takes a step of history besides, which is the other half of why
   * it is the right door: ⌘Z is how a row dropped in the wrong group comes back.
   *
   * The demo is an ordinary row and files like any other — the id the row
   * carries is the id the door is handed, and nothing here knows which one that
   * is (`groupRecent` says the same thing at itself).
   *
   * MEMBERSHIP ONLY. Where in a group a row was let go says nothing: groups stay
   * in the order the pane derives and rows inside one stay newest first. See the
   * header of `fileDrag.ts` for why a hand-sorted list would be the first thing
   * in this pane that could be wrong.
   */
  const fileRow = (row: Moving, project: string | null) => {
    const label = projectLabel(project) ?? null
    /* DROPPED WHERE IT ALREADY WAS, which is not a filing and must not be
       written as one. Both doors take it badly in their own way: `setProject`
       marks a step of history, so ⌘Z would then undo a move that never happened,
       and `fileStored` no-ops on disk but the toast and the list re-read here
       still announce one. Nobody dropping a row back in its own group asked for
       either. Said rather than silent, because a reader who cannot see the row
       land needs to know the gesture ended. */
    if (label === row.from) {
      setSay('Left where it was.')
      return
    }
    if (row.id === sessionId) {
      setProject(label)
    } else if (!fileStored(row.id, label)) {
      // Every way this fails leaves the row exactly where it was: nothing under
      // that id, a forget landed next door, or a browser that would not take the
      // write.
      toast('That exploration could not be filed.', 'bad')
      setSay('That exploration could not be filed.')
      return
    }
    bump()
    // One sentence, said twice on purpose: the toast is for whoever can see it
    // and the live region is for whoever cannot, and a reader who filed by
    // keyboard would otherwise get no confirmation at all.
    const said = label ? `Filed under “${label}”.` : 'Moved to Recent.'
    toast(said, 'good')
    setSay(said)
  }

  /**
   * The groups as boxes, asked of the DOM at the moment they are needed.
   *
   * Measured rather than tracked: the list scrolls under a drag — by hand and by
   * the auto-scroll below — so a set of rectangles taken once at the start would
   * be answering about where the groups used to be. `data-drop` marks a droppable
   * group and `data-project` carries its label, absent for the unfiled bucket,
   * which is the same `null` `groupRecent` uses.
   */
  const measure = (): DropBox[] => {
    const list = listRef.current
    const clip = scroller()
    if (!list || !clip) return []
    const lr = clip.getBoundingClientRect()
    const raw = [...list.querySelectorAll<HTMLElement>('[data-drop]')].map((el) => {
      const r = el.getBoundingClientRect()
      return {
        project: el.dataset.project ?? null,
        left: r.left,
        right: r.right,
        top: r.top,
        bottom: r.bottom,
      }
    })
    // Clipped to the column they live in — see `visibleBoxes`. A group scrolled
    // out of the list still HAS a rectangle, and an unclipped one sits behind the
    // tool rows above the list.
    return visibleBoxes(raw, {
      project: null,
      left: lr.left,
      right: lr.right,
      top: lr.top,
      bottom: lr.bottom,
    })
  }

  /**
   * The boxes as of the last time anything moved them, and the one place a drag
   * asks about them.
   *
   * MEASURED ON EVENTS, not on movement. `getBoundingClientRect` forces layout,
   * and doing it once per group per `pointermove` — plus again on every
   * auto-scroll frame — is a synchronous reflow storm on the one gesture that
   * has to stay smooth. Nothing about a group's box changes while a pointer
   * travels over it: only a scroll moves them, and there are exactly two of those
   * (the list's own, and this drag's auto-scroll), both of which say so.
   */
  const remeasure = () => {
    boxesRef.current = measure()
    return boxesRef.current
  }

  /** Everything a move put up, taken down. Safe to call twice, and called on
   *  every exit — a drop, a cancel, an Escape, a layer opening over the top. */
  const endMove = useCallback(() => {
    detachRef.current?.()
    detachRef.current = null
    if (holdTimer.current) window.clearTimeout(holdTimer.current)
    holdTimer.current = 0
    if (frame.current) cancelAnimationFrame(frame.current)
    frame.current = 0
    pressRef.current = null
    dragRef.current = null
    grabRef.current = null
    boxesRef.current = []
    /* THE FLAG THAT SAYS "THAT PRESS WAS A DRAG" HAS TO STOP SAYING IT.
       A press that became a drag must not also open the exploration on the way
       up, and `openRow` consumes the flag to make sure it does not. But the flag
       used to sit set until something consumed it, and a drop that ends over a
       group dispatches no click on the row at all — so the next thing to reach
       `openRow` was swallowed instead, which for a keyboard is the Enter on a
       row somebody had walked to afterwards. The browser dispatches a gesture's
       click in the same turn as its pointerup, so clearing on the NEXT turn is
       after that click and before anything a person can do. */
    if (draggedRef.current) {
      window.clearTimeout(clearDragged.current)
      clearDragged.current = window.setTimeout(() => {
        draggedRef.current = false
      }, 0)
    }
    setDrag(null)
    setGrab(null)
  }, [])
  endMoveRef.current = endMove

  /** Escape ends a move and stops there — the pane, the selection and the node
   *  under it are all layers below this one. A lift counts as a layer for
   *  exactly this reason (components/useEscapeToClose.ts). */
  const cancelMove = useCallback(() => {
    const said = grabRef.current ? 'Move cancelled.' : ''
    endMove()
    if (said) setSay(said)
  }, [endMove])
  useEscapeToClose(moving, cancelMove)

  const showDrag = (next: DragState) => {
    dragRef.current = next
    setDrag(next)
  }

  /**
   * Lift a row under the pointer and start following it.
   *
   * The list scrolls itself while the pointer sits near an edge, on a frame loop
   * rather than on pointermove: a pointer held still at the bottom of a long
   * list has to keep scrolling, and a mover that only ran on movement would
   * stop the moment somebody waited for it.
   */
  const beginDrag = (from: Moving, x: number, y: number) => {
    draggedRef.current = true
    showDrag({ ...from, x, y, over: targetAt(x, y, remeasure()) })
    const tick = () => {
      frame.current = 0
      const d = dragRef.current
      const list = scroller()
      if (!d || !list) return
      const r = list.getBoundingClientRect()
      const step = autoScrollStep(d.y, r.top, r.bottom)
      if (step) {
        const was = list.scrollTop
        list.scrollTop += step
        // Only when it ACTUALLY moved: at either end of a long list the step is
        // asked for and refused, and re-measuring for a scroll that did not
        // happen is the reflow this cache exists to avoid.
        if (list.scrollTop !== was) {
          showDrag({ ...d, over: targetAt(d.x, d.y, remeasure()) })
        }
      }
      frame.current = requestAnimationFrame(tick)
    }
    frame.current = requestAnimationFrame(tick)
  }

  const onPointerMove = (e: PointerEvent) => {
    const d = dragRef.current
    if (d) {
      // Against the CACHED boxes: nothing about a group's rectangle changes
      // while a pointer travels over it, and only a scroll moves them.
      showDrag({
        ...d,
        x: e.clientX,
        y: e.clientY,
        over: targetAt(e.clientX, e.clientY, boxesRef.current),
      })
      return
    }
    const p = pressRef.current
    if (!p) return
    const dx = e.clientX - p.x
    const dy = e.clientY - p.y
    if (p.coarse) {
      // A finger that moves before the hold fires is scrolling the list, which
      // it must go on being able to do. The slop is larger than the drag
      // threshold because a finger resting is never still.
      if (Math.hypot(dx, dy) > LONG_PRESS_SLOP) endMove()
      return
    }
    if (startedDrag(dx, dy)) beginDrag(p, e.clientX, e.clientY)
  }

  const onPointerUp = () => {
    const d = dragRef.current
    if (!d) {
      endMove()
      return
    }
    const over = d.over
    endMove()
    // Dropped on nothing is a cancel. The unfiled bucket is a box like any other
    // and is NOT nothing — un-filing is a thing somebody means to do.
    if (over) fileRow(d, over.project)
  }

  /**
   * The gesture was TAKEN, not finished — and the difference is the whole of
   * this function.
   *
   * `pointercancel` is the browser saying it has decided this pointer belongs to
   * something else: the compositor took it for a scroll, the OS took it for a
   * system gesture, the touch was interrupted. It used to be wired to the same
   * handler as `pointerup`, which read every one of those as a deliberate
   * release and FILED the row wherever the ghost happened to be — a document
   * moved into a project because a phone put up a notification.
   *
   * A cancel ends the move and says so, and calls no door at all.
   */
  const onPointerCancel = () => {
    const taken = dragRef.current !== null
    endMove()
    if (taken) setSay('Cancelled.')
  }

  /**
   * A press on a row, which is not yet anything.
   *
   * On a fine pointer it becomes a drag at six pixels and stays a click below
   * that, because the press this shares an element with OPENS an exploration. On
   * a coarse one there is no threshold that can tell a drag from a scroll at its
   * first pixel, so the row is lifted by TIME and any movement before the timer
   * cancels it. `fileDrag.ts` carries both numbers and the reasoning.
   */
  const beginPress = (e: ReactPointerEvent, entry: RecentEntry, from: string | null) => {
    // The primary button only: a right-click is a context menu, not a lift.
    if (e.pointerType === 'mouse' && e.button !== 0) return
    if (grabRef.current) return
    /* ONE PRESS AT A TIME, and this is a leak rather than a nicety. A second
       pointerdown while one is already live — a second finger, which is an
       ordinary thing to do on a list — overwrote `detachRef` with the new
       press's teardown, and the first press's four window listeners and its hold
       timer were then unreachable for the life of the page. One of those
       listeners is a NON-PASSIVE `touchmove`, so the leak does not merely
       accumulate: it quietly taxes every scroll in the app afterwards. The
       second finger is refused, which is also the right answer for the gesture —
       a row is dragged by one pointer. */
    if (pressRef.current || dragRef.current) return
    draggedRef.current = false
    const coarse = e.pointerType !== 'mouse'
    pressRef.current = {
      id: entry.id,
      seed: entry.seed,
      name: entry.name,
      from,
      x: e.clientX,
      y: e.clientY,
      coarse,
    }

    /* CAPTURED TO THE ROW, and the window listeners stay where they are.
       Without capture a release outside the browser window is never delivered:
       the drag stays live, the ghost stays up, and the next click anywhere files
       the row wherever the pointer happens to be. Capture is what makes that
       pointerup arrive. It does not displace the listeners below — a captured
       pointer's events are dispatched at the capture target and still propagate
       to `window` — and the listeners want to be global anyway, because the
       ghost follows the pointer across the whole page rather than across one
       row. The belt to that brace is `blur`: if the window itself loses focus
       mid-drag the pointer may never be released to us at all, and a drag left
       standing behind another application is a drag nobody can see to cancel. */
    // Both read off the event NOW: the teardown below runs long after this
    // handler has returned, and a React event is not a thing to hold onto.
    const source = e.currentTarget
    const pointerId = e.pointerId
    try {
      source.setPointerCapture(pointerId)
    } catch {
      /* A pointer that has already gone. The window listeners still cover it. */
    }
    const move = (ev: PointerEvent) => onPointerMove(ev)
    const up = () => onPointerUp()
    const cancel = () => onPointerCancel()
    const blurred = () => onPointerCancel()
    /* Non-passive, because it does two jobs and both need the touch event
       itself rather than the pointer event derived from it.

       ONE: once a finger has lifted a row, the browser must stop scrolling the
       list under it. Only while a drag is actually live, so an ordinary flick
       still scrolls.

       TWO: a finger that moves before the hold fires is SCROLLING, and the same
       slop check `pointermove` makes is made here as well. It is not the whole
       answer — see the scroll listener below, which is — but it catches the
       gesture where the events are delivered promptly. */
    const held = (ev: TouchEvent) => {
      if (dragRef.current) {
        ev.preventDefault()
        return
      }
      const p = pressRef.current
      const t = ev.touches[0]
      if (!p || !t) return
      if (Math.hypot(t.clientX - p.x, t.clientY - p.y) > LONG_PRESS_SLOP) endMove()
    }
    /* A THIRD CANCEL, and the reason there are three is measured rather than
       defensive. Chromium hands the compositor the touch stream while it decides
       whether a gesture is a scroll, and JS can get nothing at all until that is
       settled: on a flick the first `pointermove` landed 332ms after the press
       and the whole stream was still being delivered half a second later. The
       350ms hold sits right on that boundary. Two things do arrive in time — the
       slop check above, by a few milliseconds, and the `pointercancel` Chromium
       sends when the compositor takes the gesture — and a race that close should
       not be the whole of the answer. So the SCROLL is watched as well, which is
       the honest reading anyway: a press that scrolled the list was a scroll.
       Passive, because it only observes. */
    const list = scroller()
    const scrolled = () => {
      // Before the lift, a scroll IS the cancel. After it, the scroll is this
      // drag's own auto-scroll or a wheel over the list, and it has moved every
      // group — which is one of exactly two things that can, so it is where the
      // measurement is taken again.
      if (!dragRef.current) {
        endMove()
        return
      }
      const d = dragRef.current
      showDrag({ ...d, over: targetAt(d.x, d.y, remeasure()) })
    }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
    window.addEventListener('pointercancel', cancel)
    window.addEventListener('touchmove', held, { passive: false })
    window.addEventListener('blur', blurred)
    list?.addEventListener('scroll', scrolled, { passive: true })
    detachRef.current = () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
      window.removeEventListener('pointercancel', cancel)
      window.removeEventListener('touchmove', held)
      window.removeEventListener('blur', blurred)
      list?.removeEventListener('scroll', scrolled)
      try {
        source.releasePointerCapture(pointerId)
      } catch {
        /* Already released — every ordinary end of a gesture does it for us. */
      }
    }
    if (coarse) {
      holdTimer.current = window.setTimeout(() => {
        const p = pressRef.current
        if (p) beginDrag(p, p.x, p.y)
      }, LONG_PRESS_MS)
    }
  }

  /**
   * The same act by keyboard: lift, cycle, drop.
   *
   * Not an afterthought and not a lesser path. Filing by drag alone would put a
   * whole feature behind a gesture a keyboard cannot make and a screen reader
   * cannot see, which is the one thing this repository's own bar forbids. The
   * candidates are the groups ON SCREEN, including the unfiled bucket and any
   * project that is still empty — the same set a pointer can reach, so the two
   * hands can do exactly the same things.
   */
  const lift = (entry: RecentEntry, from: string | null) => {
    showLayer(null)
    const g: Grab = { id: entry.id, seed: entry.seed, from, at: liftAt(candidates, from) }
    grabRef.current = g
    setGrab(g)
    setSay(liftSaid(nameOf(entry), grabTarget(g, candidates)))
  }

  const stepGrab = (by: number) => {
    const g = grabRef.current
    if (!g) return
    const next = { ...g, at: stepAt(g.at, by, candidates.length) }
    grabRef.current = next
    setGrab(next)
    // The target alone: the rest of the sentence has not changed, and repeating
    // it turns cycling through six groups into six paragraphs.
    setSay(targetSaid(grabTarget(next, candidates)))
  }

  const dropGrab = () => {
    const g = grabRef.current
    if (!g) return
    const target = grabTarget(g, candidates)
    endMove()
    fileRow({ id: g.id, seed: g.seed, from: g.from }, target)
  }

  /**
   * The keys a row answers while it has focus.
   *
   * `m` is unbound in `App.tsx`'s map — checked, not assumed — but the press is
   * stopped here anyway: a key this row consumes must not also reach the ladder,
   * and the next binding added over there should not silently take this one.
   * The menu's other letters ARE bound over there — `f` frames the graph, `s`
   * skips a cue, `p` runs the three lenses — and are shadowed only while a row
   * has focus, for the reason `ROW_KEYS` gives: a reader with a row under their
   * fingers means the row. ⇧F10 and the Menu key open the menu itself, which is
   * what those two keys mean everywhere.
   */
  const rowKeys = (
    e: ReactKeyboardEvent,
    entry: RecentEntry,
    from: string | null,
    pinned = false,
  ) => {
    if (e.metaKey || e.ctrlKey || e.altKey) return
    const g = grabRef.current
    if (!g || g.id !== entry.id) {
      // Row to row by arrow, Home and End (D335): each row is two tab stops, so
      // Tab alone made the fifteenth exploration thirty presses away. Over the
      // presses in DOM order — PINNED's copies first, and a folded project's
      // rows are not rendered, so they are skipped for free. Tab is unchanged
      // and there is no roving tabindex, so nothing else about focus moves.
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Home' || e.key === 'End') {
        const rows = [...(listRef.current?.querySelectorAll<HTMLElement>('.explrow__open') ?? [])]
        const to = rowStep(e.key, rows.indexOf(e.currentTarget as HTMLElement), rows.length)
        e.preventDefault()
        e.stopPropagation()
        if (to !== null) rows[to]?.focus()
        return
      }
      const key = e.key.toLowerCase()
      let act: (() => void) | null = null
      if (e.key === 'ContextMenu' || (e.shiftKey && e.key === 'F10')) {
        // The ⋮ is the row's sibling, and its own click is the one door to the
        // menu — so the keyboard presses the same button the pointer would.
        const more = e.currentTarget.parentElement?.querySelector<HTMLElement>('.explrow__more')
        act = () => more?.click()
      } else if (key === 'm') act = () => lift(entry, from)
      else if (key === 'p') act = () => togglePin(entry)
      // The field stands in for the copy that was pressed — `pinned` says which.
      else if (key === 'r') act = () => showLayer({ kind: 'rename', id: entry.id, pinned })
      else if (key === 'f') act = () => fork(entry)
      else if (key === 's') act = () => share(entry)
      if (!act) return
      e.preventDefault()
      e.stopPropagation()
      act()
      return
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      e.stopPropagation()
      stepGrab(e.key === 'ArrowDown' ? 1 : -1)
      return
    }
    if (e.key === 'Enter' || e.key === ' ') {
      // preventDefault or the button's own click opens the exploration on the
      // way past — the drop and the open are the same key on the same element.
      e.preventDefault()
      e.stopPropagation()
      dropGrab()
    }
  }

  /** The group a drop would land in right now, by either hand, or null for
   *  none. One value, so the highlight cannot say two different things. */
  const over: { project: string | null } | null = drag
    ? drag.over
    : grab
      ? { project: grabTarget(grab, candidates) }
      : null

  /* ── The row's menu ────────────────────────────────────────────────────── */

  /**
   * Shut the menu and put focus back on the ⋮ that opened it.
   *
   * The button stays mounted through every act but Forget, whose confirm
   * replaces the row: a focus() on a detached button is a no-op, and that one
   * case lands where the trash glyph used to land it. Read off the ref BEFORE
   * `showLayer` clears it, which is the whole reason the anchor is a ref and
   * not part of `menu`.
   */
  const closeMenu = useCallback(() => {
    const anchor = menuAnchor.current
    showLayer(null)
    anchor?.focus()
  }, [showLayer])
  useEscapeToClose(menu !== null, closeMenu)
  useEscapeToClose(projectMenu !== null, closeMenu)

  /**
   * Open one row's menu on its ⋮ — or shut it, if that is the ⋮ it is on.
   *
   * Through `showLayer`, so it is one of the pane's exclusive layers: a lift in
   * progress ends, the models popover shuts, and Escape has one thing to take
   * down. A pinned row is drawn twice and each copy has a ⋮; `pinned` says
   * which, so the toggle and the row's `aria-expanded` agree about which
   * button is open. A right-click hands the pointer too, and opens there
   * rather than toggling (D368).
   */
  const openMenu = (e: RecentEntry, pinned: boolean, anchor: HTMLElement, at?: Point) => {
    if (!at && menu?.id === e.id && menu.pinned === pinned) {
      closeMenu()
      return
    }
    showLayer({ kind: 'menu', id: e.id, pinned, anchor, at })
  }

  /**
   * Open a project heading's menu (D368): by a right-click, at the pointer; by
   * ⇧F10 or the Menu key, against the heading's button that had focus; or on
   * touch by its ⋮, which toggles it (D375). The anchor is where focus goes back
   * to, as the ⋮ is for a row's. A right-click's is the fold, or on an empty
   * project the +, because a fine pointer draws no ⋮ to go back to.
   */
  const openProjectMenu = (project: string, anchor: HTMLElement, at?: Point) => {
    if (!at && projectMenu?.project === project) {
      closeMenu()
      return
    }
    showLayer({ kind: 'projectMenu', project, anchor, at })
  }

  /** The group the open project menu is about, off the same read as the list.
   *  It shuts by itself when the heading is gone — an empty project removed, or
   *  its last row filed away in another tab. */
  const projectMenuGroup =
    projectMenu !== null ? (named.find((g) => g.project === projectMenu.project) ?? null) : null
  useEffect(() => {
    if (projectMenu && !projectMenuGroup) showLayer(null)
  }, [projectMenu, projectMenuGroup, showLayer])

  /** The row the open menu belongs to, off the same read as the list. The menu
   *  shuts by itself when that row is gone, which a forget in another tab can
   *  do underneath it. */
  const menuEntry = menu ? (recents.find((e) => e.id === menu.id) ?? null) : null
  useEffect(() => {
    if (menu && !menuEntry) showLayer(null)
  }, [menu, menuEntry, showLayer])

  // A press away shuts it, like the models popover. So do a scroll and a
  // resize: a scroll under an anchored menu leaves it hanging where the row
  // used to be, and a resize moves the window's edges it was placed against.
  // The list is one press from opening it again where the row now is.
  // Capture for the scroll, because the list's own scroll never bubbles.
  //
  // A ⋮ is spared the press away, because its own click is the toggle that
  // shuts the menu — a row's, and a heading's on touch (D375). A heading's other
  // anchors are not: the fold folds and the + makes, so a press on either shuts
  // the heading's menu like any press away.
  const rowMenuUp = menu !== null
  const projectMenuUp = projectMenu !== null
  useEffect(() => {
    if (!rowMenuUp && !projectMenuUp) return
    const inMenu = (t: EventTarget | null) =>
      t instanceof Node && (document.getElementById(menuId)?.contains(t) ?? false)
    const away = (e: PointerEvent) => {
      const anchor = menuAnchor.current
      if (anchor?.matches('.explrow__more, .explgroup__more') && anchor.contains(e.target as Node))
        return
      if (inMenu(e.target)) return
      showLayer(null)
    }
    const moved = (e: Event) => {
      if (inMenu(e.target)) return
      showLayer(null)
    }
    window.addEventListener('pointerdown', away)
    window.addEventListener('scroll', moved, true)
    window.addEventListener('resize', moved)
    return () => {
      window.removeEventListener('pointerdown', away)
      window.removeEventListener('scroll', moved, true)
      window.removeEventListener('resize', moved)
    }
  }, [rowMenuUp, projectMenuUp, menuId, showLayer])

  /**
   * The ⋮ owed the focus after an act re-drew its row somewhere else.
   *
   * `closeMenu` hands focus back to the button that opened the menu, and for
   * most acts that is the end of it. A FILING moves the row under another
   * heading, which React remounts, so the button just focused is detached by
   * the time the list is re-read and focus has fallen to `<body>`; a fork of
   * an unfiled row files the source too; and an unpin from the PINNED copy
   * takes that copy off the screen. Each of those bumps `wrote`, so the answer
   * is one commit later, off that signal — and only when focus actually fell:
   * a request left standing would take the focus at whatever unrelated moment
   * the list changed next (`removeProject` records the same hazard).
   */
  const wantMore = useRef<{ id: string; pinned: boolean } | null>(null)
  useEffect(() => {
    const want = wantMore.current
    if (!want) return
    wantMore.current = null
    const at = document.activeElement
    if (at && at !== document.body && at.isConnected) return
    listRef.current
      ?.querySelector<HTMLElement>(
        `.explrow__more[data-more="${CSS.escape(want.id)}"][data-copy="${want.pinned ? 'pinned' : 'group'}"]`,
      )
      ?.focus()
  }, [wrote])

  /**
   * One of the menu's items, pressed.
   *
   * The menu is shut FIRST and focus put back on the ⋮, so an act that opens a
   * sheet — Share — has that button as the thing `useModalFocus` captures to
   * restore to, and an act that switches the session — Open, Fork — leaves
   * focus on a row that is still there. Forget then arms the row's own
   * confirm, the same one the trash glyph armed: the one act here that another
   * pass cannot undo is confirmed where it was asked, never from a menu.
   */
  const menuAct = (e: RecentEntry, pinned: boolean, act: RowAct) => {
    closeMenu()
    switch (act) {
      case 'open':
        openRow(e)
        return
      case 'pin':
      case 'unpin':
        // The copy under the heading is the one that is still there afterwards.
        wantMore.current = { id: e.id, pinned: false }
        togglePin(e)
        return
      case 'rename':
        showLayer({ kind: 'rename', id: e.id, pinned })
        return
      case 'fork':
        wantMore.current = { id: e.id, pinned }
        fork(e)
        return
      case 'share':
        share(e)
        return
      case 'forget':
        showLayer({ kind: 'forget', id: e.id, pinned })
        return
    }
  }

  /**
   * Keep, on a row's armed Forget — by its button or by Escape. The confirm
   * stood in for the row, so the ⋮ that armed it is a new button by the time the
   * row is back: the seat above puts focus on it, off the same signal a filing
   * uses, rather than leaving a keyboard reader on `<body>` (D335).
   */
  const keepRow = (e: RecentEntry, pinned: boolean) => {
    wantMore.current = { id: e.id, pinned }
    showLayer(null)
    bump()
  }

  /** The submenu's choice — the same door a drop and a keyboard lift land on,
   *  with the same "left where it was" when the choice is the row's own group. */
  const menuMove = (e: RecentEntry, pinned: boolean, project: string | null) => {
    closeMenu()
    wantMore.current = { id: e.id, pinned }
    fileRow({ id: e.id, seed: e.seed, from: groupOf(e) }, project)
  }

  /* ── A project's menu (D368) ───────────────────────────────────────────── */

  /**
   * One of a project menu's items, pressed. Each is a door the heading already
   * had, restated where a right-click finds it — the +, the fold, the ✕ — but
   * Rename, which is new, and is the one act on a project that no row can do.
   * The menu shuts first and focus goes back to the heading's button, as
   * `menuAct` does for a row; Rename then swaps the heading for its field.
   */
  const projectAct = (project: string, act: ProjectAct) => {
    closeMenu()
    switch (act) {
      case 'new':
        startIn(project)
        return
      case 'fold':
      case 'unfold':
        toggleProjectFold(project)
        return
      case 'rename':
        showLayer({ kind: 'renameProject', project })
        return
      case 'remove':
        removeProject(project)
        return
    }
  }

  /**
   * Give a project another name, which is to say file every one of its rows
   * under the new label — a project IS a label on each document, with nothing
   * else to rename (`core/projects.ts`).
   *
   * Through `fileRow`'s two writers, for `fileRow`'s reason: the OPEN
   * exploration through the store, and every other through `fileStored`. The
   * open one takes no step of history: a rename is not a filing of that one
   * row, and a ⌘Z that took back only its label would split the project in two
   * under a name nothing else wears. An EMPTY project has no rows to carry the
   * label, so its registry entry is swapped instead; a fold follows the label.
   *
   * A name another project already has, in any case, is refused and that
   * project lit — `commitProject`'s answer, for `commitProject`'s reason: two
   * headings a letter's case apart are the near-duplicate the check exists to
   * prevent, and a merge is not what anybody typing a name asked for. A row
   * whose write fails stays where it was, and the sentence counts them.
   */
  const renameProject = (from: string, typed: string) => {
    showLayer(null)
    wantHead.current = from
    const to = projectLabel(typed)
    if (!to || to === from) {
      bump()
      setSay('Name left as it was.')
      return
    }
    const clash = allLabels.find((p) => p !== from && p.toLowerCase() === to.toLowerCase())
    if (clash) {
      bump()
      flashGroup(clash)
      const said = `“${clash}” is already here.`
      toast(said, 'info')
      setSay(said)
      return
    }
    let stayed = 0
    for (const e of recents) {
      if (groupOf(e) !== from) continue
      if (e.id === sessionId) setProject(to, { history: false })
      else if (!fileStored(e.id, to)) stayed++
    }
    if (declaredProjects.includes(from)) {
      undeclareProject(from)
      declareProject(to)
    }
    // The new label folds exactly as the old one did. A fold list may still hold
    // `to` from a project long emptied, so it is set rather than toggled blind.
    const wasFolded = isFolded(from)
    if (wasFolded) toggleProjectFold(from)
    if (foldedSet.has(to) !== wasFolded) toggleProjectFold(to)
    wantHead.current = to
    bump()
    flashGroup(to)
    const said =
      stayed === 0
        ? `Renamed “${from}” to “${to}”.`
        : `Renamed “${from}” to “${to}” — ${stayed} exploration${stayed === 1 ? '' : 's'} could not be moved and stayed under “${from}”.`
    toast(said, stayed === 0 ? 'good' : 'warn')
    setSay(said)
  }

  /** The name field, left as it was: Escape. Focus goes back to the heading. */
  const unrenameProject = (project: string) => {
    showLayer(null)
    wantHead.current = project
    bump()
  }

  /**
   * The heading owed the focus after its name field went — `wantMore`'s rule
   * for a heading: its fold, or on an empty project its +, under the name it
   * has now, and only if focus fell.
   */
  const wantHead = useRef<string | null>(null)
  useEffect(() => {
    const want = wantHead.current
    if (want === null) return
    wantHead.current = null
    const at = document.activeElement
    if (at && at !== document.body && at.isConnected) return
    const p = CSS.escape(want)
    listRef.current
      ?.querySelector<HTMLElement>(
        `.explgroup__fold[data-project="${p}"], .explgroup__plus[data-project="${p}"]`,
      )
      ?.focus()
  }, [wrote])

  /* ── Making a project ──────────────────────────────────────────────────── */

  /**
   * Light a group and put it on screen.
   *
   * What "that one already exists" looks like: naming a project that is already
   * there takes you to it instead of drawing a second heading with the same
   * words. Also what a NEW one does on arrival, because an empty group added to
   * the top of a scrolled list is otherwise a press that appears to do nothing.
   */
  const flashGroup = (label: string) => {
    setFlash(label)
    window.clearTimeout(flashTimer.current)
    flashTimer.current = window.setTimeout(() => setFlash(null), 1400)
  }

  /* Scrolled ONCE, when the flash is set, and never while a row is in flight.
     It was a callback ref on the group itself, which React re-attaches on every
     render — so the list was yanked back to that heading on every save and,
     worst, on each frame of a drag's own auto-scroll, which is a fight between
     two things scrolling the same box. An effect keyed on the
     label runs when the label changes and not otherwise. */
  useEffect(() => {
    if (flash === null || dragRef.current) return
    listRef.current
      ?.querySelector<HTMLElement>('[data-flash]')
      ?.scrollIntoView({ block: 'nearest' })
  }, [flash])

  const closeNaming = () => {
    setNaming(false)
    setProjectDraft('')
    projectBtnRef.current?.focus()
  }

  /**
   * Remove an empty project, and put focus somewhere on purpose.
   *
   * The ✕ unmounts the heading it is on, which strands focus on `<body>` — the
   * finding D22 records against the pill, and this file hands focus on
   * deliberately everywhere else it destroys the control that was pressed. The
   * next empty group's own ✕ if there is one, because somebody clearing up is
   * usually clearing up more than one; otherwise the button that makes them,
   * which is where `closeNaming` lands too.
   */
  const removeProject = (label: string) => {
    // Nothing to remove is nothing to hand focus on for: the effect below waits
    // on the list actually changing, and a request left standing would take the
    // focus at whatever unrelated moment it changed next.
    if (!declaredProjects.includes(label)) return
    const marks = [...(listRef.current?.querySelectorAll<HTMLElement>('.explgroup__x') ?? [])]
    const i = marks.findIndex((el) => el.dataset.project === label)
    const next = i >= 0 ? (marks[i + 1] ?? marks[i - 1]) : undefined
    undeclareProject(label)
    // One commit later: the row this focus is meant for is being unmounted now,
    // and the survivors have not been laid out again yet.
    wantAfterRemove.current = next?.dataset.project ?? null
  }

  useEffect(() => {
    const want = wantAfterRemove.current
    if (want === undefined) return
    wantAfterRemove.current = undefined
    const mark =
      want === null
        ? null
        : listRef.current?.querySelector<HTMLElement>(
            `.explgroup__x[data-project="${CSS.escape(want)}"]`,
          )
    if (mark) mark.focus()
    else projectBtnRef.current?.focus()
  }, [declaredProjects])

  /**
   * Make an empty project, or go to the one that is already there.
   *
   * Matched case-INSENSITIVELY, against every label on the device: "battery
   * chemistry" typed over an existing "Battery chemistry" is somebody reaching
   * for that group, and
   * answering with a second heading beside it is the near-duplicate this whole
   * check exists to prevent.
   *
   * The name goes through `projectLabel`, the ONE clamp, so a project made here
   * and a project a fork mints are the same string and therefore the same group.
   */
  const commitProject = () => {
    const label = projectLabel(projectDraft)
    if (!label) {
      closeNaming()
      return
    }
    const already = allLabels.find((p) => p.toLowerCase() === label.toLowerCase())
    closeNaming()
    if (already) {
      flashGroup(already)
      toast(`“${already}” is already here.`, 'info')
      return
    }
    if (!declareProject(label)) return
    flashGroup(label)
    toast(`Project “${label}”. Drag explorations in.`, 'good')
  }

  /**
   * Forget one exploration, off either shelf — through `forgetExploration`, which
   * every Forget door shares (D331). It clears the canvas first when this is the
   * one open, settles the save that clearing queued, deletes and waits for the
   * shelf to accept, and only then takes the transcripts; a refusal reopens what
   * it cleared and removes nothing. This door says what the shelf answered.
   *
   * The confirm stays up until then, showing the press as pending, and comes down
   * only if it is still the one on screen — a reader who opened another layer in
   * the meantime keeps it. A press the lock refused leaves it up, as it always
   * did: the lock's own sentence has already been said.
   */
  const forget = (id: string) => {
    void forgetExploration(id).then((out) => {
      if (out.kind === 'kept' && out.reason === 'busy') return
      setConfirming((c) => (c?.id === id ? null : c))
      bump()
      const line = forgetLine(out)
      if (line) toast(line.text, line.tone)
    })
  }

  /* ── Models ────────────────────────────────────────────────────────────── */

  const entries = useMemo(() => routedEntries({ modelId, roleModels }), [modelId, roleModels])
  // Never simplified to `keys[p] !== null`: a custom endpoint's readiness is an
  // address, not a key, and the short version reports a working local server as
  // IDLE — then the row says "no key" over a model that would have answered.
  const liveFor = (provider: string) =>
    provider === 'custom' ? customBase.trim().length > 0 : keys[provider as 'anthropic'] !== null
  const liveCount = entries.filter((e) => liveFor(e.provider)).length
  const routed = Object.keys(roleModels).length > 0

  const modelText = modelRowText(liveCount, routed, entries[0]?.name ?? null)

  const closeModels = useCallback(() => {
    showLayer(null)
    modelsRowRef.current?.focus()
  }, [showLayer])
  useEscapeToClose(modelsOpen, closeModels)

  // Opening lands on the model already chosen — the press that opened the
  // popover is on a row that stays mounted, so only this direction needs the
  // effect; closing focuses the row straight from the handler.
  useEffect(() => {
    if (modelsOpen) radioRef.current?.focus()
  }, [modelsOpen])

  useEffect(() => {
    if (!modelsOpen) return
    const away = (e: PointerEvent) => {
      const t = e.target as Node
      if (modelsRowRef.current?.contains(t)) return
      if (document.getElementById(modelsId)?.contains(t)) return
      showLayer(null)
    }
    window.addEventListener('pointerdown', away)
    return () => window.removeEventListener('pointerdown', away)
  }, [modelsOpen, modelsId, showLayer])

  /** Move the SELECTION only. Never `roleModels`: a hand-routed role keeps its
   *  model, which is why a routed row leads to Routing instead. */
  const pick = (m: ModelEntry) => {
    setModel(m.id)
    closeModels()
    if (!liveFor(m.provider)) {
      toast(`${m.name} has no key — the next pass will refuse until you add one.`, 'warn')
    } else if (routed) {
      toast(`Next pass runs on ${m.name}. Roles you routed by hand keep their own model.`)
    } else {
      toast(`Next pass runs on ${m.name}.`)
    }
  }

  /* ── The profile name ──────────────────────────────────────────────────── */

  const startEditName = () => {
    cancelName.current = false
    setNameDraft(profileName)
    setEditingName(true)
  }
  const commitName = () => {
    if (cancelName.current) return
    setProfileName(nameDraft)
  }
  const leaveName = () => {
    wantProfileRow.current = true
    setEditingName(false)
  }

  // The field is mounted one commit after the press, so the focus has to wait
  // for it — a straight `.focus()` in the handler is a silent no-op that strands
  // focus on `<body>` (D22 records the same finding on the pill).
  useEffect(() => {
    if (naming) projectRef.current?.focus()
  }, [naming])

  useEffect(() => {
    if (editingName) {
      nameRef.current?.focus()
      nameRef.current?.select()
      return
    }
    if (!wantProfileRow.current) return
    wantProfileRow.current = false
    profileRef.current?.focus()
  }, [editingName])

  /* ── Chrome ────────────────────────────────────────────────────────────── */

  /**
   * The tools, as an element rather than inline JSX, only so the render below
   * reads as the column it is: brand row, tools, the scroll region, the footer.
   * They render in exactly one place, whatever the viewport's height — see
   * `COMPACT_MAX_H` for why a second placement was the wrong answer to a short
   * screen.
   */
  const doors = (
    <>
      {/* THE MAKER, THEN THE DOORS. New exploration first, just under the app's
          name — the reference's order, and the one act a first run needs
          before any door (D102). Then where the pane goes besides its list:
          Research Lab, the documents a source check may search, and everything
          saved on this device — the three things here that are about the
          WORKSPACE rather than about the list — fixed, on the rows' own idiom
          rather than a stack of outlined buttons. The sheets they open inert
          the stage over this pane and hand focus back to the row that opened
          them, so none of them closes the phone's overlay — `share` below
          carries the reasoning. Research Lab's row is the same door the rail
          carries as a glyph, so folding the pane never changes whether the
          exploration's instruments exist.

          Four rows, and the count is a height budget: on a coarse pointer each
          is 40px, and with the brand row and the footer this fixed stack is
          about 300px. Under `COMPACT_MAX_H` nothing is pinned and the whole
          column scrolls instead — a style on the column, never a move in the
          tree. New project is not a row here at all: it is the + on the
          PROJECTS eyebrow, inside the list it adds to. */}
      <div
        style={{
          flex: 'none',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          padding: '4px 8px',
        }}
      >
        <ToolRow
          icon="plus"
          label="New exploration"
          hint="⌘N"
          title="New exploration  ⌘N"
          primary
          onPress={() => {
            // The shared door — one lock for this row, the palette's row and
            // ⌘N, with the guard and the toast inside it.
            if (newExploration() && narrow) setPhoneOpen(false)
          }}
        />
        <ResearchLabDoor row open={researchOpen} onOpen={() => setResearchOpen(true)} />
        <ToolRow
          icon="file"
          label={SOURCES_DOOR}
          title="Paste documents a source check should search and cite"
          onPress={() => setSources(true)}
        />
        <ToolRow
          icon="library"
          label="Library"
          title="Everything saved on this device, and what saving means here"
          onPress={() => setLibrary(true)}
        />
      </div>
    </>
  )

  /**
   * One group's rows, with the confirm standing in for the row whose Forget is
   * armed. Shared by the named groups and the unfiled bucket, so the two
   * sections cannot come to draw a row differently — D102 split them, because
   * PROJECTS draws always, for the + on its eyebrow, and RECENT only over rows.
   */
  const rowsOf = (g: RecentGroup) =>
    g.entries.map((e) =>
      renaming?.id === e.id && !renaming.pinned ? (
        <RenameField
          key={e.id}
          entry={e}
          onCommit={(typed) => rename(e, false, typed)}
          onCancel={() => unname(e, false)}
        />
      ) : confirming?.id === e.id && !confirming.pinned ? (
        <ForgetConfirm
          key={e.id}
          id={e.id}
          name={nameOf(e)}
          outcomes={e.outcomes}
          isOpen={e.id === sessionId}
          onForget={() => forget(e.id)}
          onKeep={() => keepRow(e, false)}
        />
      ) : (
        <Row
          key={e.id}
          entry={e}
          openId={sessionId}
          mark={markOf(e)}
          twin={twins.has(nameOf(e))}
          menuOpen={menu?.id === e.id && !menu.pinned}
          lifted={grab?.id === e.id}
          dragging={drag?.id === e.id}
          onPress={(ev) => beginPress(ev, e, g.project)}
          onKeys={(ev) => rowKeys(ev, e, g.project)}
          onOpen={() => openRow(e)}
          onMore={(el, at) => openMenu(e, false, el, at)}
        />
      ),
    )

  const shell = {
    position: 'absolute' as const,
    // Desktop: docked flush to the window — full height, hard against the left
    // edge, so it reads as a sidebar rather than a floating rounded card. The
    // phone overlay keeps its inset and slide (below), where a full-bleed sheet
    // dropped over the graph would be a different, heavier thing than an opener
    // you tap and dismiss.
    left: narrow ? PANE_INSET : 0,
    top: narrow ? 8 : 0,
    bottom: narrow ? 8 : 0,
    // Flush left means the pane spans the whole reserved column, the old
    // PANE_INSET margin included — its right edge still lands on the inset
    // App computed, so `sidebarInset` and everything measured from it
    // (chromeLeft, viewInset.left, the camera) agree with the box. The inset
    // is read as the custom property, with App's number as the fallback: the
    // seam runs ahead of the number while a drag is live (D104).
    width: narrow ? 'min(300px, 86vw)' : `var(${SIDEBAR_INSET_VAR}, ${rail ? RAIL_W : width}px)`,
    zIndex: 'var(--z-float)' as unknown as number,
    pointerEvents: 'auto' as const,
    ...(narrow ? {} : { borderRight: '1px solid var(--hairline)' }),
    // The width IS the animation. Everything the pane displaces — the session
    // column, the camera's inset, the ledger dock, the minimap — rides the same
    // 380ms, so the field opens and closes as one movement rather than five.
    // The overlay arrives instead of growing: it displaces nothing. And the
    // seam takes the slide away for the life of a drag (`SIDEBAR_SLIDE_VAR`):
    // a box that eased toward the pointer would lag the hand moving it.
    ...(narrow
      ? { animation: 'sidebarSlide 0.22s cubic-bezier(0.22, 1, 0.36, 1) both' }
      : { transition: `width var(${SIDEBAR_SLIDE_VAR}, ${SLIDE})` }),
  }

  // The phone's door, where the pill used to be. Rendered instead of the pane
  // rather than beside it: two openings for one list at the width with the least
  // room is the confusion this whole posture avoids.
  if (narrow && !phoneOpen) {
    return (
      <button
        ref={openerRef}
        className="btn btn--icon"
        aria-label={`Explorations${foldedWords(folded)}`}
        title="Explorations"
        onClick={() => expand()}
        // A button on the field, so the glass flat and the button's corner, as
        // the design draws the one it floats over its graph (D357).
        style={{
          ...glass('flat'),
          position: 'absolute',
          left: 16,
          top: 16,
          width: 40,
          height: 40,
          borderRadius: 'var(--r-sm)',
          zIndex: 'var(--z-float)' as unknown as number,
          pointerEvents: 'auto',
        }}
      >
        <Icon name="library" size={15} />
        <FoldedDot folded={folded} inset={5} />
      </button>
    )
  }

  return (
    <GlassPane
      ariaLabel="Explorations"
      shellStyle={shell}
      radius={narrow ? undefined : 0}
      // Docked, the pane is a column beside the field rather than glass over it
      // (D353): its own ground, and one hairline on the field's side. The phone's
      // overlay floats over the graph, and keeps the glass.
      solid={narrow ? undefined : 'var(--pane-side)'}
    >
      {/* The measured pane and the dismissal's "inside" are the same box. The
          shell is `GlassPane`, which takes no ref, so the contains() test
          reads this wrapper — it fills the shell, so the two rectangles agree. */}
      <div
        ref={paneRef}
        // Under COMPACT_MAX_H this column is the scroller and the list below is
        // not: the same nodes, one style apart, so nothing is unmounted at the
        // crossing. overscrollBehavior for the same reason the list carries it.
        className={compact ? 'scroll' : undefined}
        style={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          height: '100%',
          overflowY: compact ? 'auto' : undefined,
          overscrollBehavior: compact ? 'contain' : undefined,
        }}
      >
        {rail ? (
          <Rail
            folded={folded}
            ready={ready}
            keyless={liveCount === 0}
            profileName={profileName}
            researchOpen={researchOpen}
            onExpand={expand}
            onSearch={() => setSearchOpen(true)}
            onNew={() => newExploration()}
            onResearch={() => setResearchOpen(true)}
            onSettings={() => setSetup('settings')}
          />
        ) : (
          <>
            {/* The pane opens on the app's own name rather than on the word for
                its list: a column that carries the tools as well as the list is
                the app's front door, and a front door says whose it is. Plain
                text, not a heading element — the shell is already the landmark
                named "Explorations", and a second name on the same box would
                have a reader hear two. */}
            <div className="sb-brand">
              {/* The app's mark, the one the thread's byline wears (D353). */}
              <span className="sb-brand-name">
                <i className="sb-mark" aria-hidden />
                Pathways
              </span>
              <span className="sb-brand-acts">
                {/* THE GLASS, beside the collapse button — the reference's
                    corner, and the one place the search is found from (D102).
                    It opens a sheet rather than a box in the column: the sheet
                    lists what matches instead of filtering the list under the
                    pointer, so the column keeps every row where it was. The
                    rail carries the same door as a glyph. */}
                <button
                  className="btn btn--icon"
                  aria-label="Search explorations"
                  title="Search explorations"
                  onClick={() => setSearchOpen(true)}
                >
                  <Icon name="search" size={15} />
                </button>
                <button
                  className="btn btn--icon"
                  aria-label={narrow ? 'Close the explorations list' : 'Collapse the sidebar'}
                  title={narrow ? 'Close' : 'Collapse the sidebar  ⌘['}
                  onClick={shut}
                >
                  <Icon name="panelLeft" size={15} />
                </button>
              </span>
            </div>
            {/* What this library has promised and not yet answered. Above the
                list because it is about every row in it, and closed by default:
                a person who has recorded no dates never sees it at all. */}
            <DueList />

            {doors}

            {/* overscrollBehavior is not cosmetic: without it a flick past the
                end of the list chains to the canvas and pans the graph under the
                pane somebody is reading. Under COMPACT_MAX_H the list gives up
                being a scroller — `flex: none`, overflow visible — and the
                column above takes over, so a short screen scrolls everything
                rather than clipping the list to nothing. */}
            <div
              ref={listRef}
              className="scroll"
              style={{
                flex: compact ? 'none' : 1,
                minHeight: 0,
                overflowY: compact ? 'visible' : 'auto',
                overscrollBehavior: 'contain',
                padding: '0 8px 8px',
              }}
            >
              {/* THE PINNED SECTION, first, and only while it has members:
                  D89's objection to the reference's Pinned heading was that
                  a heading over an empty set names nothing, and it still
                  does. NOT a drop target — no `data-drop`, so `measure`
                  never sees it and a row let go over it is a cancel. A pin
                  is not a filing, and a box that took drops would have to
                  mean one of the two. The rows are the same component as
                  the ones under the projects, drawn again, saying the
                  project they belong to in their hover and their name rather
                  than on a line under it (D390): a row drawn away from its
                  heading has lost the one thing the layout said about it. */}
              {pinnedShown.length > 0 && (
                <>
                  <div className="eyebrow explsection" role="heading" aria-level={2}>
                    Pinned
                  </div>
                  <div className="explgroup">
                    {pinnedShown.map((e) =>
                      renaming?.id === e.id && renaming.pinned ? (
                        <RenameField
                          key={e.id}
                          entry={e}
                          onCommit={(typed) => rename(e, true, typed)}
                          onCancel={() => unname(e, true)}
                        />
                      ) : confirming?.id === e.id && confirming.pinned ? (
                        <ForgetConfirm
                          key={e.id}
                          id={e.id}
                          name={nameOf(e)}
                          outcomes={e.outcomes}
                          isOpen={e.id === sessionId}
                          onForget={() => forget(e.id)}
                          onKeep={() => keepRow(e, true)}
                        />
                      ) : (
                        <Row
                          key={e.id}
                          entry={e}
                          openId={sessionId}
                          inPinned
                          mark={markOf(e)}
                          twin={twins.has(nameOf(e))}
                          menuOpen={menu?.id === e.id && menu.pinned}
                          lifted={grab?.id === e.id}
                          dragging={drag?.id === e.id}
                          onPress={(ev) => beginPress(ev, e, groupOf(e))}
                          onKeys={(ev) => rowKeys(ev, e, groupOf(e), true)}
                          onOpen={() => openRow(e)}
                          onMore={(el, at) => openMenu(e, true, el, at)}
                        />
                      ),
                    )}
                  </div>
                </>
              )}

              {/* THE PROJECTS SECTION. Its eyebrow draws ALWAYS, with or without
                  a project under it, because it carries the + that makes one
                  (D102): a small plus at the row's far right, on the line every
                  heading's own + takes, where the pane used to spend a whole
                  row on "New project". D89's
                  objection to a heading over an empty set — that it names
                  nothing — does not reach a heading that is also a door. The
                  name field opens under the eyebrow, in the place the first
                  heading will take, and the empty projects stand first (see
                  `shown`). Outside the groups' boxes on purpose: a section
                  label is not a place a row can be dropped. */}
              <div className="eyebrow explsection explsection--projects">
                {/* A heading to a screen reader, as it is to the eye: the pane's
                    two lists were two words of text, so a heading jump went from
                    the page's name straight to the card. */}
                <span role="heading" aria-level={2}>
                  Projects
                </span>
                <button
                  ref={projectBtnRef}
                  className="btn btn--icon explsection__plus"
                  aria-label="New project"
                  title="New project — a heading with nothing under it yet; drag explorations in, or press its +"
                  onClick={() => {
                    setProjectDraft('')
                    setNaming(true)
                  }}
                >
                  <Icon name="plus" size={13} />
                </button>
              </div>
              {/* A project with no members has nowhere to live in a model where
                  a project IS a label on a document, so the name typed here is
                  written into the small registry `core/projects.ts` keeps, and
                  the empty group it draws is what the first exploration gets
                  dragged into — or made in, since every heading carries a + of
                  its own. */}
              {naming && (
                <input
                  ref={projectRef}
                  className="input"
                  type="text"
                  value={projectDraft}
                  placeholder="Project name"
                  aria-label="New project name"
                  maxLength={PROJECT_LABEL_MAX}
                  onChange={(e) => setProjectDraft(e.target.value)}
                  // Escape belongs to the field while the caret is in it: the
                  // press stops here rather than reaching the ladder to
                  // deselect the node under the graph. Capture plus
                  // stopPropagation, the pane's own contract.
                  onKeyDownCapture={(e) => {
                    if (e.key === 'Escape') {
                      e.stopPropagation()
                      closeNaming()
                      return
                    }
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      commitProject()
                    }
                  }}
                  // Cancels rather than commits, which is the one place this
                  // differs from the profile name below. A name is about this
                  // device and can be retyped; making a project is a change to
                  // the shape of the list, and a stray click that adds a heading
                  // somebody was still deciding about is a mess they have to
                  // notice before they can undo it.
                  onBlur={closeNaming}
                  // Where the first heading will stand, so the field arrives
                  // where the project will.
                  style={{ margin: '2px 2px 6px', width: 'calc(100% - 4px)' }}
                />
              )}
              {/* Nothing under the eyebrow while there is no project: its + is
                  the one control that changes that, and it says so itself
                  (D390). A sentence here restated the +. */}
              {named.map((g) => (
                // Keyed through `projectKey`, which is where the reason lives:
                // a group's key is user-authored text. `data-drop` is what
                // `measure` looks for and `data-project` carries the label. The
                // whole group is the target, heading and rows alike: a drop
                // aimed at a heading and one aimed at the space under it mean
                // the same thing, and two zones would only be two ways to miss.
                <div
                  key={projectKey(g.project)}
                  className="explgroup"
                  data-drop=""
                  data-project={g.project}
                  data-over={over && over.project === g.project ? '' : undefined}
                  data-flash={flash !== null && flash === g.project ? '' : undefined}
                >
                  {/* The heading, or its name field while it is being renamed
                      — standing in for it, as a row's field does for the row. */}
                  {renamingProject === g.project ? (
                    <ProjectNameField
                      label={g.project}
                      onCommit={(typed) => renameProject(g.project, typed)}
                      onCancel={() => unrenameProject(g.project)}
                    />
                  ) : (
                    <ProjectHead
                      label={g.project}
                      folded={isFolded(g.project)}
                      menuOpen={projectMenu?.project === g.project}
                      onToggle={
                        g.entries.length > 0 ? () => toggleProjectFold(g.project) : undefined
                      }
                      onNew={() => startIn(g.project)}
                      onRemove={g.entries.length === 0 ? () => removeProject(g.project) : undefined}
                      onMenu={(anchor, at) => openProjectMenu(g.project, anchor, at)}
                    />
                  )}
                  {/* A folded project keeps its heading — the +, the drop — and
                      draws nothing under it (D101). Nor does an empty one: its
                      open folder and its + say what it is and what it takes,
                      and the hint that restated them is gone (D390). The
                      heading is the whole drop target either way. */}
                  {g.entries.length > 0 && !isFolded(g.project) && rowsOf(g)}
                </div>
              ))}

              {/* THE RECENT SECTION: the unfiled rows, last, under an eyebrow of
                  their own. A place and not a project, so no folder, no count
                  and no + — its + would be New exploration at the head of the
                  pane. Only while there are unfiled rows: an empty bucket has
                  nothing to say, and nor does a fresh device, whose way in is
                  New exploration at the head of the pane (D390). The box carries `data-drop` and no
                  `data-project`, so it reads back as the same `null`
                  `groupRecent` uses — which is how a row comes back OUT of a
                  project. */}
              {unfiled ? (
                <>
                  <div className="eyebrow explsection" role="heading" aria-level={2}>
                    Recent
                  </div>
                  <div
                    className="explgroup"
                    data-drop=""
                    data-over={over && over.project === null ? '' : undefined}
                  >
                    {rowsOf(unfiled)}
                  </div>
                </>
              ) : null}
            </div>

            <div className="sb-foot">
              {modelsOpen && (
                <ModelsPopover
                  id={modelsId}
                  entries={entries}
                  modelId={modelId}
                  roleModels={roleModels}
                  routed={routed}
                  ready={ready}
                  liveFor={liveFor}
                  radioRef={radioRef}
                  onPick={pick}
                  onRouting={() => {
                    closeModels()
                    setSetup('settings', 'routing')
                  }}
                  onSettings={() => {
                    closeModels()
                    setSetup('settings')
                  }}
                />
              )}
              {/* No aria-label, deliberately: the visible text becomes this
                  button's computed name. ONE LINE (D390), where the design drew
                  two: keyless it is the act, "Connect a model", beside the
                  profile's "Set your name"; live it is what is connected, and a
                  reader hears "Model:" before it, which the eye gets from the
                  card's place. The keyless dot is the accent, the app's ink for
                  a press it asks for — the scent D22 gave the word, moved to the
                  mark. */}
              <button
                ref={modelsRowRef}
                className="sb-model"
                data-keyless={liveCount === 0 || undefined}
                aria-expanded={modelsOpen}
                aria-controls={modelsOpen ? modelsId : undefined}
                aria-haspopup="dialog"
                onClick={() => (modelsOpen ? closeModels() : showLayer({ kind: 'models' }))}
              >
                <i className="sb-model-dot" aria-hidden />
                <span className="sb-model-text">
                  {liveCount === 0 ? (
                    <span className="sb-model-name">Connect a model</span>
                  ) : (
                    <span className="sb-model-name">
                      <span className="sr-only">Model: </span>
                      {modelText}
                    </span>
                  )}
                </span>
                {/* Pointing on, as the design draws it: the card is a door. */}
                <Icon name="chevron" size={13} style={{ flex: 'none', opacity: 0.6 }} />
              </button>

              {editingName ? (
                <input
                  ref={nameRef}
                  className="input"
                  aria-label="Profile name"
                  value={nameDraft}
                  placeholder="Your name"
                  maxLength={60}
                  onChange={(e) => setNameDraft(e.target.value)}
                  // Enter commits, Escape cancels, and leaving the field commits
                  // — the same three answers a rename anywhere else gives. The
                  // cancel flag is load-bearing: Escape unmounts the input, and
                  // whether that fires a blur is the browser's business rather
                  // than something this file should depend on.
                  onKeyDownCapture={(e) => {
                    if (e.key === 'Escape') {
                      e.stopPropagation()
                      cancelName.current = true
                      leaveName()
                      return
                    }
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      commitName()
                      leaveName()
                    }
                  }}
                  onBlur={() => {
                    commitName()
                    setEditingName(false)
                  }}
                  style={{ marginTop: 4 }}
                />
              ) : (
                <button
                  ref={profileRef}
                  className="sb-profile"
                  onClick={startEditName}
                  title="Set the name this device goes by"
                >
                  <Avatar name={profileName} />
                  <span className="sb-profile-name" data-unset={!profileName || undefined}>
                    {profileName || 'Set your name'}
                  </span>
                </button>
              )}
            </div>
          </>
        )}

        {/* What a reader is told, and the only channel they have for a gesture
            that is otherwise entirely visual. Polite: a lift, a target and a
            drop are all reports, and none of them may cut across what somebody
            is already being read. */}
        <div aria-live="polite" className="sr-only">
          {say}
        </div>
      </div>

      {/* THE GHOST, through a portal to the body — `Floating` in Composer.tsx
          carries the full reasoning for the same move: this shell clips its own
          overflow, and the stage around it is a containing block for fixed
          children. A ghost rendered inside it would be placed against the stage
          and cut off at the pane's edge the moment somebody dragged onto the
          field. */}
      {drag &&
        createPortal(
          <div className="explghost" aria-hidden style={{ left: drag.x, top: drag.y }}>
            <span className="explghost__dot" />
            {nameOf(drag)}
          </div>,
          document.body,
        )}

      {/* THE MENU, through the same portal and for the same reason — and one
          more: the list scrolls, so a menu inside it would be clipped at the
          list's edge on the last rows, which are exactly where a long list
          puts the row somebody is reaching for. */}
      {menu &&
        menuEntry &&
        createPortal(
          <RowMenu
            id={menuId}
            name={nameOf(menuEntry)}
            items={rowMenuItems({
              isOpen: menuEntry.id === sessionId,
              pinned: pinnedSet.has(menuEntry.id),
              candidates,
              from: groupOf(menuEntry),
            })}
            anchor={menuAnchor.current}
            at={menu.at}
            onAct={(act) => menuAct(menuEntry, menu.pinned, act)}
            onMove={(project) => menuMove(menuEntry, menu.pinned, project)}
            onClose={closeMenu}
          />,
          document.body,
        )}

      {/* A PROJECT'S MENU (D368): the same component, keys and portal, over
          the acts on the heading rather than on a row. */}
      {projectMenu &&
        projectMenuGroup &&
        createPortal(
          <RowMenu
            id={menuId}
            name={projectMenuGroup.project}
            items={projectMenuItems({
              members: projectMenuGroup.entries.length,
              folded: isFolded(projectMenuGroup.project),
            })}
            anchor={menuAnchor.current}
            at={projectMenu.at}
            onAct={(act) => projectAct(projectMenuGroup.project, act)}
            onClose={closeMenu}
          />,
          document.body,
        )}

      {/* THE SEAM, last so it draws over the regions it rides (D104). The
          expanded desktop column only: the rail has nothing to size, and the
          phone's overlay is a fixed width by design. Its bounds are the ones
          App clamps the width with; while a drag is live it paints the inset
          property the pane and the thread column both read, and takes the
          collapse's slide away so neither box eases toward the hand. */}
      {!narrow && !rail && (
        <PaneSeam
          width={width}
          min={sidebarBounds(stageW, reservedRight, sidebarFit).min}
          max={sidebarBounds(stageW, reservedRight, sidebarFit).max}
          step={SIDEBAR_STEP}
          label="Resize the explorations sidebar"
          paint={(w) => document.documentElement.style.setProperty(SIDEBAR_INSET_VAR, `${w}px`)}
          commit={setSidebarWidth}
          stepBy={(by) => stepSidebarWidth(width, by, stageW, reservedRight, sidebarFit)}
          onDragging={(live) => {
            const root = document.documentElement.style
            if (live) root.setProperty(SIDEBAR_SLIDE_VAR, '0s')
            else root.removeProperty(SIDEBAR_SLIDE_VAR)
          }}
        />
      )}
    </GlassPane>
  )
}

/** The initials, or the design's question mark. Never a letter this app was not given. */
function Avatar({ name, size = 26 }: { name: string; size?: number }) {
  const initials = initialsOf(name)
  return (
    <span
      aria-hidden
      style={{
        width: size,
        height: size,
        flex: 'none',
        display: 'grid',
        placeItems: 'center',
        borderRadius: '50%',
        background: 'var(--surface-active)',
        fontSize: size > 24 ? '11px' : '9px',
        fontWeight: 'var(--fw-medium)',
        color: 'var(--text-2)',
      }}
    >
      {/* The design's mark for a device with no name: a question, never an
          initial this app was not given. */}
      {initials || '?'}
    </span>
  )
}

/**
 * A row, armed.
 *
 * Armed in place. The library confirms destructive acts on the row that
 * triggered them rather than in a dialog, and a delete reachable from the
 * sidebar has to meet the same bar — this is the one act here that another pass
 * cannot undo.
 *
 * One component and one sentence, wherever the row is — and since D335 the
 * library's sentence too (`forgetSentence`): a second copy of this paragraph is
 * how a rarer surface comes to promise something softer than the common one, and
 * this, the common one, was the copy that left out the recorded results.
 *
 * It holds to the app's own confirm pattern (`ProviderKeyCard`'s Remove): Keep
 * takes the focus, because the menu's Forget left it on a ⋮ this confirm has
 * just replaced and a keyboard reader was on `<body>`; Escape is Keep; the
 * sentence is in the refuted ink and Forget in the bad tone, as the menu item
 * that armed it is. Forget stays first, where a hand already reaches for it.
 */
function ForgetConfirm({
  id,
  name,
  outcomes,
  isOpen,
  onForget,
  onKeep,
}: {
  id: string
  /** The row's name, as the row prints it (D105) — the confirm replaces that
   *  row in place, so it must name the same thing the same way. */
  name: string
  /** The row's recorded results, which the sentence names when there are any. */
  outcomes?: number
  isOpen: boolean
  onForget: () => void
  onKeep: () => void
}) {
  // Pending until the shelf answers. Both buttons stand down meanwhile: a second
  // Forget would ask again for an answer already coming, and Keep would promise
  // something the press can no longer take back.
  const pending = useForgetting(id)
  const keepRef = useRef<HTMLButtonElement | null>(null)
  // Only where focus has fallen, as `ProviderKeyCard` guards it: a confirm that
  // arrived some other way must not take the focus from wherever it is.
  useEffect(() => {
    const at = document.activeElement
    if (at && at !== document.body && at.isConnected) return
    keepRef.current?.focus()
  }, [])
  return (
    <div
      // The rename field's contract: Escape belongs to the confirm while focus
      // is in it, stopped here before the ladder sees it.
      onKeyDownCapture={(e) => {
        if (e.key !== 'Escape') return
        e.stopPropagation()
        if (!pending) onKeep()
      }}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: '11px 12px',
        borderRadius: 'var(--r-lg)',
        background: 'var(--surface-sunken)',
      }}
    >
      <span style={{ fontSize: '11px', lineHeight: 1.45, color: 'var(--ink-refuted)' }}>
        {forgetSentence({ name, outcomes, isOpen })}
      </span>
      <div style={{ display: 'flex', gap: 6 }}>
        <button className="btn btn--bad" onClick={onForget} disabled={pending} aria-busy={pending}>
          {pending ? 'Forgetting…' : 'Forget'}
        </button>
        <button ref={keepRef} className="btn" onClick={onKeep} disabled={pending}>
          Keep
        </button>
      </div>
    </div>
  )
}

/**
 * A row's name, open for typing — the menu's Rename, R on a focused row, and the
 * palette's Rename row (through `sigNaming`). Stands in for the row it names, as
 * the Forget confirm does, so the field is where the name was.
 *
 * It arrives holding the name the row prints, selected, so typing replaces it and
 * an arrow key edits it. The placeholder is the seed's own clamp — what the row
 * reads with no name at all — so emptying the field shows, before Enter, exactly
 * what clearing gives back, which is why no hint stands under it (D390). Enter
 * keeps it, Escape leaves it as it was, and a
 * press elsewhere keeps it too, as the profile name does: a name can be retyped,
 * and on the open exploration ⌘Z takes it back. The rule for what is kept is
 * `nameFor`'s, not this field's: the seed's own words, or nothing, are no name.
 *
 * Exported for its test, like the row.
 */
export function RenameField({
  entry,
  onCommit,
  onCancel,
}: {
  entry: RecentEntry
  onCommit: (typed: string) => void
  onCancel: () => void
}) {
  const [draft, setDraft] = useState(() => shownName(entry))
  /** Settled once, by whichever of Enter, Escape or the blur came first: the
   *  field unmounts as it settles, and a blur on the way out must not settle it
   *  a second time — least of all as a commit after an Escape. */
  const settled = useRef(false)
  const settle = (keep: boolean) => {
    if (settled.current) return
    settled.current = true
    if (keep) onCommit(draft)
    else onCancel()
  }
  const seedName = explorationName(entry.seed) || 'Untitled exploration'
  return (
    <div
      className="explrow"
      style={{ flexDirection: 'column', alignItems: 'stretch', padding: '4px' }}
    >
      <input
        className="input"
        type="text"
        value={draft}
        placeholder={seedName}
        aria-label={`Name for “${nameOf(entry)}”`}
        maxLength={NAME_CHARS}
        // Selected on arrival: the reader asked to rename, and the first key they
        // type is the new name, not an edit tacked on the end of the old one.
        autoFocus
        onFocus={(e) => e.currentTarget.select()}
        onChange={(e) => setDraft(e.target.value)}
        // The project field's contract: Escape and Enter belong to the field
        // while the caret is in it, stopped here before the ladder sees them.
        onKeyDownCapture={(e) => {
          if (e.key === 'Escape') {
            e.stopPropagation()
            settle(false)
            return
          }
          if (e.key === 'Enter') {
            e.preventDefault()
            settle(true)
          }
        }}
        onBlur={() => settle(true)}
      />
    </div>
  )
}

/** The words for a row's mark, from the app's one table of status words — so a
 *  screen reader hears what the eye is shown, in the words the app uses for
 *  that state everywhere else (D334). */
const MARK_WORD: Record<RowMark, string> = {
  running: STATUS_WORD.running,
  unread: STATUS_WORD.unread,
}

/**
 * One stored exploration.
 *
 * A div rather than a button: the row carries one more button, the ⋮, and a
 * button inside a button is invalid HTML — the browser closes the outer one
 * and the row's own press stops existing.
 *
 * The ⋮ is the row's ONE control besides its press (D100). It is a menu
 * button in the ARIA sense, and says so, and while its menu is up the row
 * keeps it on screen (`data-menu`) — the pointer is in the menu by then, so
 * hover cannot. Exported for its test, like the heading and the tool row.
 */
export function Row({
  entry,
  openId,
  inPinned = false,
  mark,
  menuOpen = false,
  twin = false,
  lifted,
  dragging,
  onPress,
  onKeys,
  onOpen,
  onMore,
}: {
  entry: RecentEntry
  openId: string
  /** The copy in the PINNED section. It prints the project it is drawn away
   *  from — the one thing the layout stopped saying about it (D99). Neither
   *  copy prints the word "pinned" (D103): the section says it for this one,
   *  and the menu's Unpin says it for the other — so the row is not told
   *  whether it is pinned at all; the menu is built by the pane from the
   *  pinned set, not by the row. */
  inPinned?: boolean
  /** Which mark the row wears: a ring while something is arriving in it, a
   *  filled dot while it is unread, nothing at rest (D334). Decided by the pane
   *  through `rowMark`, never by the row, so both copies of a row and the
   *  search sheet agree. */
  mark?: RowMark
  /** Whether THIS copy's ⋮ has its menu up. */
  menuOpen?: boolean
  /** Whether another stored exploration carries the same name. Decided by the
   *  pane over the whole index, never by the row, which sees only itself. */
  twin?: boolean
  /** Held by the keyboard. Drawn, and said in the row's own name, because a
   *  reader who cannot see the outline still has to know what is in hand. */
  lifted?: boolean
  /** Under the pointer. The row stays in place and dims — the ghost is what
   *  moves, so the list never reflows while somebody is aiming at it. */
  dragging?: boolean
  onPress?: (e: ReactPointerEvent) => void
  onKeys?: (e: ReactKeyboardEvent) => void
  onOpen: () => void
  /** The ⋮, pressed. Handed the button itself: the menu hangs from it and
   *  focus comes back to it. A right-click hands the pointer as well, and the
   *  menu opens there instead (D368). */
  onMore: (anchor: HTMLElement, at?: Point) => void
}) {
  const isOpen = entry.id === openId
  const name = nameOf(entry)
  // Only on the pinned copy, and through the one clamp, so the row says the
  // same string the heading it is drawn away from would.
  const where = inPinned ? projectLabel(entry.project) : undefined
  /** What tells this row apart (`about`, below): only what nothing else says. A
   *  row whose name another row shares is the one case where the count and the
   *  age DO say something nothing else does — four identical names were four
   *  rows nobody could tell apart — so a twin carries them, and only a twin.
   *
   *  A FORK SAYS WHERE IT CAME FROM INSTEAD, twin or not: "branch at step 12 ·
   *  24 Sep" is the answer the count and the age were a proxy for, and it does
   *  not go stale. Its source, still a twin, keeps the count and the age — so a
   *  family of forks reads as one original and its dated branches. The fork's
   *  line reads only this row, so the row decides it; whether a name is shared
   *  is still the pane's to say. */
  // Kept true while the row is on screen: it read "6m ago" for as long as nothing
  // else re-rendered the pane (`useAgo`).
  const age = useAgo(!entry.forkedFrom && twin ? entry.savedAt : null)
  const told = entry.forkedFrom
    ? forkLine(entry.forkedFrom)
    : twin
      ? `${entry.nodes} node${entry.nodes === 1 ? '' : 's'} · ${age}`
      : null
  // A copy opened from a link and not yet continued says so on its row too, in
  // the read-first line's own word (`SharedCopy.readFirst`).
  const shared = entry.shared ? STATUS_WORD.sharedCopy.toLowerCase() : null
  /** What tells this row from another, said and never printed (D390). It was a
   *  second line under the name — the demo's pill, a pinned copy's project, a
   *  twin's count and age, a fork's origin, a shared copy's word — and the owner
   *  cut every line under a name: a row is its name. The words are kept where
   *  they cost no line: in the hover's title under the seed, and in the press's
   *  name, so a reader and an agent reading the page still hear what tells two
   *  same-named rows apart. Where they still cannot be told apart at a glance,
   *  Rename is the act that does it, one press away on the ⋮. */
  const about = [entry.demo ? 'Demo' : null, where, told, shared]
    .filter((m): m is string => !!m)
    .join(' · ')
  // The whole seed where the name is a clamp of it (D105), and what tells the
  // row apart under it: a hover is where a one-line row says everything else.
  const hover = [name !== entry.seed ? entry.seed : '', about].filter(Boolean).join('\n')
  return (
    <div
      className="explrow"
      data-open={isOpen || undefined}
      data-lifted={lifted || undefined}
      data-dragging={dragging || undefined}
      data-menu={menuOpen || undefined}
      // A right-click is the ⋮, as ⇧F10 and the Menu key are (D335): the whole
      // row is the target and focus still comes back to the ⋮, but the menu
      // opens at the pointer, where a right-click aims (D368). Never on touch,
      // where a long press is the lift. Not over its own open menu: the Menu
      // key opens it on keydown and some browsers fire this after.
      onContextMenu={(e) => {
        if (!rightClickOpensMenu((e.nativeEvent as PointerEvent).pointerType, finePointer())) return
        e.preventDefault()
        const more = e.currentTarget.querySelector<HTMLElement>('.explrow__more')
        if (more && !menuOpen) onMore(more, { x: e.clientX, y: e.clientY })
      }}
    >
      <button
        className="explrow__open"
        title={hover || undefined}
        aria-current={isOpen || undefined}
        // Said rather than only drawn: an outline is not a state a reader can
        // hear, and this one changes what every arrow key does.
        aria-label={lifted ? `Moving "${name}"` : undefined}
        onPointerDown={onPress}
        onKeyDown={onKeys}
        onClick={onOpen}
      >
        {/* THE MARK (D334, after D103): nothing at rest, a ring while a run
            or an answer is arriving here, and a filled dot once one landed
            and the exploration has not been opened since — opening puts it
            out. The slot is always there, so a name never moves when a mark
            comes. The stylesheet owns the look; the row says only which mark
            is up, and says it in words below for a reader who cannot see it. */}
        <span aria-hidden className="explrow__dot" data-mark={mark} />
        <span style={{ flex: 1, minWidth: 0 }}>
          <span
            // The class is the measure's: the pane reads every name's laid-out
            // width off these to find where its seam should stop.
            className="explrow__name"
          >
            {name}
          </span>
        </span>
        {/* What tells the row apart, said (D390): outside the name, whose text
            ranges the seam's measure reads, and joined to the press's name the
            way the mark's word is. `explrow__about` is its hook for a test and
            an agent, which read it where the eye once read the second line. */}
        {about && <span className="sr-only explrow__about">, {about}</span>}
        {/* The mark, said: the dot is drawn for the eye only, and a run that
            finished unseen is the one thing this row has to tell. Outside the
            name, whose text ranges the seam's measure reads, and joined to the
            press's name the way `aria-current` says "open". */}
        {mark && <span className="sr-only">, {MARK_WORD[mark]}</span>}
      </button>
      <button
        className="btn btn--icon explrow__more"
        aria-label={`Actions for "${name}"`}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        title="Open, pin, fork, share, move or forget"
        data-more={entry.id}
        data-copy={inPinned ? 'pinned' : 'group'}
        onClick={(e) => onMore(e.currentTarget)}
      >
        <Icon name="more" size={13} />
      </button>
    </div>
  )
}

/**
 * The row's menu — and a project heading's, which is the same menu over the
 * acts on a project (D368): one set of keys, one look, one portal.
 *
 * A menu in the ARIA sense — `role="menu"`, items that are `menuitem`s, one
 * highlight the arrow keys move and the pointer follows — rather than a dialog
 * of buttons: a reader arriving on a menu knows the keys before hearing a word
 * of it. Focus rides the highlight (each item is focused as it is reached),
 * Tab shuts it, Escape shuts it through the pane's own hook, and the letters
 * printed beside the items fire them.
 *
 * MOVE TO PROJECT IS A SUBMENU: → or Enter opens it beside the item, ← comes
 * back, and its highlight starts on the group the row is in now. One level
 * only, and Escape takes the whole menu down rather than the submenu alone —
 * the accepted cost of one press meaning one thing everywhere in this pane.
 * The submenu is a SIBLING of the menu in the DOM, not a child: the menu rises
 * in on a transform, which while it runs is a containing block for fixed
 * descendants, and it scrolls its own overflow, so a submenu inside it would be
 * placed against the menu's corner rather than the window's, or cut by its
 * scroll. The wrapper that holds both carries the id
 * the pane's dismissals test against and the keydown both answer to.
 *
 * Placed by `placeMenu` after a layout measurement, so it hangs where the ⋮ is
 * and flips above when the room below has run out — or by `placeAtPoint`, at
 * the pointer a right-click landed on (D368); hidden until it has been
 * placed, so it never paints at the corner for a frame. Exported for its test,
 * which renders it without a browser and reads the markup — so nothing here
 * touches `document` or `window` outside an effect.
 */
export function RowMenu<A extends string>({
  id,
  name,
  items,
  anchor,
  at,
  onAct,
  onMove,
  onClose,
}: {
  id: string
  name: string
  items: MenuItem<A>[]
  /** The ⋮ the menu hangs from. Null only in a test, which never places it. */
  anchor: HTMLElement | null
  /** Where a right-click landed: the menu opens there instead (D368). */
  at?: Point
  onAct: (act: A) => void
  /** Move to project's choice. A menu with no move item has no use for one. */
  onMove?: (project: string | null) => void
  onClose: () => void
}) {
  const rootRef = useRef<HTMLDivElement | null>(null)
  const subRef = useRef<HTMLDivElement | null>(null)
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([])
  const subRefs = useRef<(HTMLButtonElement | null)[]>([])
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null)
  const [active, setActive] = useState(() => stepPressable(items, -1, 1))
  /** The submenu, while it is up: which target the highlight is on, and where
   *  it was placed once measured. */
  const [sub, setSub] = useState<{ at: number; pos: { left: number; top: number } | null } | null>(
    null,
  )
  const moveAt = items.findIndex((it) => it.kind === 'move')
  const move = moveAt >= 0 ? (items[moveAt] as Extract<MenuItem<A>, { kind: 'move' }>) : null

  const viewport = () => ({ width: window.innerWidth, height: window.innerHeight })

  // Against the ⋮, or at the pointer, once this has a size to place.
  useLayoutEffect(() => {
    const el = rootRef.current
    if (!el || (!anchor && !at)) return
    const r = el.getBoundingClientRect()
    const size = { width: r.width, height: r.height }
    const p = at
      ? placeAtPoint(at, size, viewport())
      : placeMenu(anchor!.getBoundingClientRect(), size, viewport())
    setPos({ left: p.left, top: p.top })
  }, [anchor, at, items.length])

  // Beside its item, once the submenu has a size to place.
  useLayoutEffect(() => {
    if (!sub || sub.pos) return
    const el = subRef.current
    const item = itemRefs.current[moveAt]
    const root = rootRef.current
    if (!el || !item || !root) return
    const r = el.getBoundingClientRect()
    const p = placeSubmenu(
      item.getBoundingClientRect(),
      root.getBoundingClientRect(),
      { width: r.width, height: r.height },
      viewport(),
    )
    setSub({ at: sub.at, pos: { left: p.left, top: p.top } })
  }, [sub, moveAt])

  // Focus rides the highlight, in whichever menu is up — once that menu has
  // been PLACED. Until then it is `visibility: hidden`, and a hidden element
  // refuses focus silently: the first cut focused on mount, a frame before the
  // placement landed, and the menu opened with focus still on the ⋮.
  useEffect(() => {
    if (sub) {
      if (sub.pos) subRefs.current[sub.at]?.focus()
    } else if (pos) itemRefs.current[active]?.focus()
  }, [active, sub, pos])

  const openSub = () => {
    if (move) setSub({ at: subStart(move.targets), pos: null })
  }
  const closeSub = () => {
    setSub(null)
    setActive(moveAt)
  }

  const onKey = (e: ReactKeyboardEvent) => {
    if (e.metaKey || e.ctrlKey || e.altKey) return
    const key = e.key
    if (key === 'Tab') {
      // A menu is not a place Tab walks through: it shuts, and focus goes back
      // to the ⋮ rather than to whatever the body has after this portal.
      e.preventDefault()
      onClose()
      return
    }
    if (sub && move) {
      const n = move.targets.length
      if (key === 'ArrowDown' || key === 'ArrowUp') {
        e.preventDefault()
        setSub({ ...sub, at: stepAt(sub.at, key === 'ArrowDown' ? 1 : -1, n) })
      } else if (key === 'Home' || key === 'End') {
        e.preventDefault()
        setSub({ ...sub, at: key === 'Home' ? 0 : n - 1 })
      } else if (key === 'ArrowLeft') {
        e.preventDefault()
        closeSub()
      }
      // Enter and Space are the focused target's own click.
      return
    }
    if (key === 'ArrowDown' || key === 'ArrowUp') {
      e.preventDefault()
      setActive(stepPressable(items, active, key === 'ArrowDown' ? 1 : -1))
      return
    }
    if (key === 'Home' || key === 'End') {
      e.preventDefault()
      setActive(stepPressable(items, -1, key === 'Home' ? 1 : -1))
      return
    }
    if (key === 'ArrowRight' && items[active]?.kind === 'move') {
      e.preventDefault()
      openSub()
      return
    }
    // The letters printed beside the items.
    const k = key.toLowerCase()
    for (const it of items) {
      if (it.kind === 'act' && it.hint?.toLowerCase() === k) {
        e.preventDefault()
        onAct(it.act)
        return
      }
      if (it.kind === 'move' && it.hint.toLowerCase() === k) {
        e.preventDefault()
        openSub()
        return
      }
    }
  }

  const sheet = (p: { left: number; top: number } | null) => ({
    ...glass(),
    position: 'fixed' as const,
    left: p?.left ?? 0,
    top: p?.top ?? 0,
    visibility: (p ? 'visible' : 'hidden') as 'visible' | 'hidden',
    padding: MENU_PAD,
  })

  return (
    <div id={id} onKeyDown={onKey} style={{ display: 'contents' }}>
      <div
        ref={rootRef}
        role="menu"
        aria-label={`Actions for “${name}”`}
        className="explmenu"
        style={sheet(pos)}
      >
        {items.map((it, i) =>
          it.kind === 'sep' ? (
            <div key={i} role="separator" className="explmenu__sep" />
          ) : it.kind === 'act' ? (
            <button
              key={i}
              ref={(el) => {
                itemRefs.current[i] = el
              }}
              role="menuitem"
              tabIndex={-1}
              className="explmenu__item"
              data-active={active === i && !sub ? '' : undefined}
              data-tone={it.tone}
              onPointerEnter={() => {
                setActive(i)
                if (sub) setSub(null)
              }}
              onClick={() => onAct(it.act)}
            >
              <span className="explmenu__label">{it.label}</span>
              {it.hint && <span className="num explmenu__hint">{it.hint}</span>}
            </button>
          ) : (
            <button
              key={i}
              ref={(el) => {
                itemRefs.current[i] = el
              }}
              role="menuitem"
              tabIndex={-1}
              aria-haspopup="menu"
              aria-expanded={sub !== null}
              className="explmenu__item"
              data-active={active === i ? '' : undefined}
              onPointerEnter={() => {
                setActive(i)
                if (!sub) openSub()
              }}
              // Opens, and never toggles: the pointer arriving on this item has
              // already opened the submenu through `onPointerEnter`, and a
              // click that toggled would shut what the hover just opened — on
              // a finger, where the tap is both events, on every single tap.
              onClick={() => {
                if (!sub) openSub()
              }}
            >
              <span className="explmenu__label">{it.label}</span>
              <span className="num explmenu__hint">{it.hint}</span>
              <Icon name="chevron" size={9} className="explmenu__chev" />
            </button>
          ),
        )}
      </div>
      {sub && move && (
        <div
          ref={subRef}
          role="menu"
          aria-label="Move to project"
          className="explmenu explmenu--sub"
          style={sheet(sub.pos)}
        >
          {move.targets.map((t, j) => (
            /* Keyed through `projectKey`, as the groups are: a target's key is
               user-authored text and the unfiled bucket must not collide with a
               project somebody named "unfiled". */
            <button
              key={projectKey(t.project)}
              ref={(el) => {
                subRefs.current[j] = el
              }}
              role="menuitemradio"
              aria-checked={t.current}
              tabIndex={-1}
              className="explmenu__item"
              data-active={sub.at === j ? '' : undefined}
              onPointerEnter={() => setSub({ ...sub, at: j })}
              onClick={() => onMove?.(t.project)}
            >
              <span className="explmenu__label">{t.label}</span>
              {t.current && <Icon name="check" size={13} className="explmenu__check" />}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * One door in the tools column: a glyph, its name and, where one exists, the
 * chord that opens the same door from the keyboard.
 *
 * A button on the ROW's idiom rather than `.btn`'s — see `.expltool` in the
 * stylesheet for why a stack of outlined buttons was the wrong shape here.
 * `title` is the palette's own subtitle for the same door wherever the palette
 * has one, so a hover here and a search there read the same sentence.
 */
export function ToolRow({
  icon,
  label,
  hint,
  title,
  primary = false,
  onPress,
}: {
  icon: IconName
  label: string
  hint?: string
  title?: string
  /** The maker, New exploration: the one door drawn as a button (D353), so the
   *  pane's first act reads as the act it is and the rows under it as places. */
  primary?: boolean
  onPress: () => void
}) {
  return (
    <button
      className={primary ? 'expltool expltool--new' : 'expltool'}
      title={title}
      onClick={onPress}
    >
      <Icon name={icon} size={15} />
      <span className="expltool__label">{label}</span>
      {hint && <span className="num expltool__hint">{hint}</span>}
    </button>
  )
}

/**
 * A project's heading: the folder, the name, and the acts on the
 * project ITSELF — which is what separates them from the acts on a row.
 *
 * THE HEADING IS THE FOLD (D101, D368). With members under it, the folder AND
 * the name are one button: press either and the rows fold away under the
 * heading while the folder shuts, press again and both come back;
 * `aria-expanded` says which, and the name is the button's name, so a reader
 * hears "Battery chemistry, collapsed" — the disclosure every file tree says.
 * D101 put the fold on the 13px glyph alone, and nobody found it: the name is
 * where a press on a folder aims. An EMPTY project keeps the plain mark and a
 * plain name — there is nothing to hide but its hint, and a folder that shut
 * on nothing would be a control that lies about having done something.
 *
 * The + starts a new exploration in this project. It goes through the shared
 * door with the label, so the fresh canvas is born filed under the heading it
 * was made from and the seed lands there (`newExploration`, `startSession`).
 * It stands outside the fold's button, so a press aimed at it never folds.
 *
 * The ✕ only while the project is EMPTY, and only where the caller says so. On a
 * group with members it would be a question about the explorations in it rather
 * than about the label, and this pane already has a door for that, on the row,
 * with a confirm.
 *
 * A RIGHT-CLICK OPENS THE PROJECT'S MENU (D368), at the pointer, as a row's
 * does; ⇧F10 and the Menu key open it against the heading's button that has
 * focus. ON TOUCH A ⋮ OPENS IT (D375), at the heading's tail, hanging from it as
 * a row's does. Only on a coarse pointer, where there is no right-click — iOS
 * fires no `contextmenu` on a long press, so an iPhone had no way to Rename —
 * and drawn always there, as the + and the ✕ are, since a finger cannot hover.
 * A fine pointer keeps the name's width and its right-click. Android's long
 * press opens the menu as well, which on a row is the lift.
 *
 * `data-project` on every act: `removeProject` finds the next ✕ to hand focus to
 * by that attribute, the menu hands focus back to the fold or the + by it, and
 * a test needs it to tell one heading's act from another's without reading the
 * label out of an aria string.
 */
export function ProjectHead({
  label,
  folded,
  menuOpen = false,
  onToggle,
  onNew,
  onRemove,
  onMenu,
}: {
  label: string
  /** Whether the rows under this heading are folded away. */
  folded: boolean
  /** Whether this heading's menu is up. */
  menuOpen?: boolean
  /** The heading, pressed. Absent on an empty project, whose folder is a mark. */
  onToggle?: () => void
  onNew: () => void
  onRemove?: () => void
  /** The heading's menu, asked for: the button focus goes back to, and where a
   *  right-click landed. */
  onMenu?: (anchor: HTMLElement, at?: Point) => void
}) {
  return (
    <div
      className="explgroup__head"
      data-menu={menuOpen || undefined}
      onContextMenu={(e) => {
        if (!onMenu) return
        const pointer = (e.nativeEvent as PointerEvent).pointerType
        if (!rightClickOpensMenu(pointer, finePointer(), false)) return
        e.preventDefault()
        const anchor = e.currentTarget.querySelector<HTMLElement>(
          '.explgroup__fold, .explgroup__plus',
        )
        if (anchor && !menuOpen) onMenu(anchor, { x: e.clientX, y: e.clientY })
      }}
      onKeyDown={(e) => {
        if (!onMenu || !(e.key === 'ContextMenu' || (e.shiftKey && e.key === 'F10'))) return
        if (!(e.target instanceof HTMLElement)) return
        e.preventDefault()
        e.stopPropagation()
        onMenu(e.target)
      }}
    >
      {onToggle ? (
        <button
          className="explgroup__fold"
          data-project={label}
          aria-expanded={!folded}
          // The whole name, where the heading clamps it, and what the press does.
          title={`${label} — ${folded ? 'show' : 'hide'} its explorations`}
          onClick={onToggle}
        >
          <FolderGlyph open={!folded} />
          <span className="explgroup__name">{label}</span>
        </button>
      ) : (
        <span className="explgroup__label" title={label}>
          <FolderGlyph open className="explgroup__glyph" />
          <span className="explgroup__name">{label}</span>
        </span>
      )}
      <button
        className="btn btn--icon explgroup__plus"
        data-project={label}
        aria-label={`New exploration in “${label}”`}
        title="New exploration in this project"
        onClick={onNew}
      >
        <Icon name="plus" size={13} />
      </button>
      {onRemove && (
        <button
          className="btn btn--icon explgroup__x"
          data-project={label}
          aria-label={`Remove the empty project "${label}"`}
          title="Remove this empty project"
          onClick={onRemove}
        >
          <Icon name="close" size={11} />
        </button>
      )}
      {/* The touch screen's door to the menu (D375); the stylesheet draws it
          only on a coarse pointer. Its own click toggles, as a row's ⋮ does. */}
      {onMenu && (
        <button
          className="btn btn--icon explgroup__more"
          data-project={label}
          aria-label={`Actions for “${label}”`}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          title="New exploration, fold, rename or remove"
          onClick={(e) => onMenu(e.currentTarget)}
        >
          <Icon name="more" size={13} />
        </button>
      )}
    </div>
  )
}

/**
 * A project's name, open for typing — its menu's Rename (D368). Stands in for
 * the heading, as a row's field stands in for the row, so the field is where
 * the name was; it arrives holding the name, selected. Enter keeps it, Escape
 * leaves it as it was, and a press elsewhere keeps it, as a row's Rename does.
 * What is kept is `projectLabel`'s, the one clamp, so a renamed project and a
 * made one are the same string for the same words. Exported for its test.
 */
export function ProjectNameField({
  label,
  onCommit,
  onCancel,
}: {
  label: string
  onCommit: (typed: string) => void
  onCancel: () => void
}) {
  const [draft, setDraft] = useState(label)
  /** Settled once — `RenameField`'s rule, for its reason. */
  const settled = useRef(false)
  const settle = (keep: boolean) => {
    if (settled.current) return
    settled.current = true
    if (keep) onCommit(draft)
    else onCancel()
  }
  return (
    <div className="explgroup__head">
      <input
        className="input"
        type="text"
        value={draft}
        placeholder="Project name"
        aria-label={`Name for the project “${label}”`}
        maxLength={PROJECT_LABEL_MAX}
        autoFocus
        onFocus={(e) => e.currentTarget.select()}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDownCapture={(e) => {
          if (e.key === 'Escape') {
            e.stopPropagation()
            settle(false)
            return
          }
          if (e.key === 'Enter') {
            e.preventDefault()
            settle(true)
          }
        }}
        onBlur={() => settle(true)}
        style={{ flex: 1, minWidth: 0, margin: '0 2px 2px -4px' }}
      />
    </div>
  )
}

/**
 * The folder on a project's heading, drawn to open and shut (D101).
 *
 * `Icon`'s `folder` is one path and cannot move. This is the same outline in
 * the same hand — the 1.2 tab radius, the 2.5→13.6 span — with the folder's
 * FRONT as a second path: sides and bottom only, so that shut it lies exactly
 * on the body's own outline and the glyph is the set's folder to the pixel,
 * and open it leans forward on a skew from its bottom edge and grows the lip
 * the body was hiding. One transition, on `transform` and `opacity`, so the
 * reduced-motion rule takes both away at once. Open is the resting state of an
 * unfolded project, as it is in the reference; shut is what a fold looks like
 * from across the pane. Exported for its test.
 */
export function FolderGlyph({
  open,
  className,
  ...rest
}: { open: boolean } & SVGProps<SVGSVGElement>) {
  return (
    <svg
      width={13}
      height={13}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className ? `explfolder ${className}` : 'explfolder'}
      data-open={open || undefined}
      {...rest}
    >
      <path d="M2.5 4.9a1.2 1.2 0 0 1 1.2-1.2h3l1.5 1.6h4.1a1.2 1.2 0 0 1 1.2 1.2v5.3a1.2 1.2 0 0 1-1.2 1.2H3.7a1.2 1.2 0 0 1-1.2-1.2z" />
      <g className="explfolder__front">
        <path d="M2.5 7.6v4.2a1.2 1.2 0 0 0 1.2 1.2h8.6a1.2 1.2 0 0 0 1.2-1.2V7.6" />
        <path className="explfolder__lip" d="M2.5 7.6h11" />
      </g>
    </svg>
  )
}

/**
 * The folded list's mark, on the button that unfolds it (D334): the rows' own
 * dot, by the rows' own stylesheet, pinned to the button's corner. Always
 * there and empty at rest, the rows' reason — so the fill transitions in
 * rather than popping, and nothing about the button moves when it comes.
 */
function FoldedDot({ folded, inset }: { folded: ReturnType<typeof foldedMark>; inset: number }) {
  return (
    <span
      aria-hidden
      className="explrow__dot"
      data-mark={folded?.mark}
      style={{ position: 'absolute', top: inset, right: inset }}
    />
  )
}

/** The same mark, said — in the button's label, because an aria-label wins
 *  over any text inside the button and an sr-only span there would be heard
 *  by nobody. In the table's words, as a row says them. */
function foldedWords(folded: ReturnType<typeof foldedMark>): string {
  if (!folded) return ''
  return folded.mark === 'unread'
    ? ` — ${folded.unread} ${MARK_WORD.unread.toLowerCase()}`
    : ` — ${MARK_WORD.running.toLowerCase()}`
}

/**
 * The collapsed column: the same doors, one glyph each.
 *
 * The keyless tint and its sentence survive the collapse, which is the whole
 * reason the settings glyph is here at all — D22 put the app's only scent
 * toward the key sheet on a surface that is always on screen, and a rail that
 * dropped it would put it behind a press. The glass opens the search sheet
 * directly (D102); it used to expand the pane and focus a box the pane no
 * longer has.
 */
function Rail({
  folded,
  ready,
  keyless,
  profileName,
  researchOpen,
  onExpand,
  onSearch,
  onNew,
  onResearch,
  onSettings,
}: {
  /** The folded list's one mark (`foldedMark`), worn by the expand button. */
  folded: ReturnType<typeof foldedMark>
  ready: boolean
  keyless: boolean
  profileName: string
  researchOpen: boolean
  onExpand: (want?: 'profile' | null) => void
  onSearch: () => void
  onNew: () => void
  onResearch: () => void
  onSettings: () => void
}) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4,
        height: '100%',
        padding: '10px 0',
      }}
    >
      <button
        className="btn btn--icon"
        aria-label={`Expand the sidebar${foldedWords(folded)}`}
        title="Expand the sidebar  ⌘["
        onClick={() => onExpand()}
      >
        <Icon name="panelRight" size={13} />
        <FoldedDot folded={folded} inset={3} />
      </button>
      <button
        className="btn btn--icon"
        aria-label="Search explorations"
        title="Search explorations"
        onClick={onSearch}
      >
        <Icon name="search" size={13} />
      </button>
      <button
        className="btn btn--icon"
        aria-label="New exploration"
        title="New exploration  ⌘N"
        onClick={onNew}
      >
        <Icon name="plus" size={13} />
      </button>
      <ResearchLabDoor open={researchOpen} onOpen={onResearch} />
      <span style={{ flex: 1 }} />
      {/* The sentence goes IN the label rather than beside the glyph: an
          aria-label wins over any text inside the button, so an sr-only span
          here would be announced by nobody. */}
      <button
        className="btn btn--icon"
        aria-label={
          keyless
            ? 'Settings — no model is live; open this to connect a key'
            : ready
              ? 'Settings'
              : 'Connect a key'
        }
        title={ready ? 'Settings' : 'Connect a key'}
        onClick={onSettings}
        style={keyless ? { color: 'var(--ink-gap)' } : undefined}
      >
        <Icon name="settings" size={13} />
      </button>
      <button
        className="btn btn--icon"
        aria-label={profileName ? `Profile — ${profileName}` : 'Set your name'}
        title={profileName || 'Set your name'}
        onClick={() => onExpand('profile')}
      >
        <Avatar name={profileName} size={22} />
      </button>
    </div>
  )
}

/**
 * Research Lab belongs to the exploration, not to the canvas camera. The same
 * door sits in the expanded pane's tools column and in the collapsed rail so
 * folding the list never changes whether the current exploration's instruments
 * exist — one component in two forms rather than two doors, so the label, the
 * glyph and the open state cannot drift between them. The e2e suite finds
 * either form by the label's opening words.
 */
function ResearchLabDoor({
  open,
  onOpen,
  row = false,
}: {
  open: boolean
  onOpen: () => void
  /** The tools column's form — a row with the name on it — rather than the
   *  rail's bare glyph. */
  row?: boolean
}) {
  // The workspace is the largest instrument in the app, so its door is behind
  // the instruments switch. Absent rather than disabled: a door that is there
  // and refuses is worse than one that is not there, and Settings says where it
  // went. Nothing about the workspace itself changes — a research record
  // already written is still in the document and still in every export.
  const instruments = useStore((s) => s.instruments)
  if (!instruments) return null
  const label = 'Research Lab — protocols, evidence, tests, versions and audit'
  if (row) {
    return (
      <button
        className={`expltool${open ? ' expltool--on' : ''}`}
        aria-label={label}
        aria-expanded={open}
        aria-haspopup="dialog"
        title={label}
        onClick={onOpen}
      >
        <Icon name="experiment" size={15} />
        <span className="expltool__label">Research Lab</span>
      </button>
    )
  }
  return (
    <button
      className={`btn btn--icon${open ? ' btn--on' : ''}`}
      aria-label={label}
      aria-expanded={open}
      aria-haspopup="dialog"
      title={label}
      onClick={onOpen}
    >
      <Icon name="experiment" size={13} />
    </button>
  )
}

/**
 * The models band, ported from the pill and pointed upward.
 *
 * One level rather than the pill's two: the row that opens this is already the
 * model status, so the list it opens onto is the choice itself. What the pill's
 * first level carried — which role went where, and that a routed role is changed
 * in Routing — is above the radiogroup rather than instead of it.
 */
function ModelsPopover({
  id,
  entries,
  modelId,
  roleModels,
  routed,
  ready,
  liveFor,
  radioRef,
  onPick,
  onRouting,
  onSettings,
}: {
  id: string
  entries: ModelEntry[]
  modelId: string
  roleModels: Partial<Record<Role, string>>
  routed: boolean
  ready: boolean
  liveFor: (provider: string) => boolean
  radioRef: RefObject<HTMLButtonElement | null>
  onPick: (m: ModelEntry) => void
  onRouting: () => void
  onSettings: () => void
}) {
  return (
    <div
      id={id}
      role="dialog"
      aria-label="Models"
      style={{
        ...glass(),
        position: 'absolute',
        left: 8,
        right: 8,
        bottom: '100%',
        marginBottom: 6,
        borderRadius: 'var(--r-glass)',
        padding: 6,
        maxHeight: 'min(50vh, 380px)',
        overflowY: 'auto',
        overscrollBehavior: 'contain',
        animation: 'sidebarRise 0.2s cubic-bezier(0.22, 1, 0.36, 1) both',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '2px 2px 4px 10px',
        }}
      >
        <span className="num" style={EYEBROW}>
          {routed ? 'Models · by role' : 'Models · automatic'}
        </span>
        {/* .btn--icon is required rather than stylistic: `@media (pointer:
            coarse)` promotes it to 40x40. With no key the words come back — a
            lone glyph does not carry that state for a first run. */}
        <button
          className={ready ? 'btn btn--icon' : 'btn'}
          aria-label={ready ? 'Settings' : 'Connect a key'}
          title={ready ? 'Settings' : 'Connect a key'}
          onClick={onSettings}
        >
          <Icon name="settings" size={13} />
          {!ready && <span>Connect a key</span>}
        </button>
      </div>

      {routed && (
        <>
          {/* Every model a role was actually pointed at, the selection included
              when a role landed on it — this block is the routing picture, and
              one drawn from "everything except the selection" would be missing
              exactly the row somebody re-pointed to the model they are already
              on. The selection is still changed below, in the radiogroup. */}
          {entries
            .map((e) => ({
              entry: e,
              roles: ROLES.filter((r) => roleModelId({ modelId, roleModels }, r) === e.id),
            }))
            .filter((r) => r.roles.length > 0)
            .map(({ entry: e, roles }) => {
              return (
                /* A routed row leads to Routing rather than swapping the
                   selection out from under a role somebody pointed by hand —
                   exactly the confusion invariant 4 exists to prevent. */
                <button
                  key={e.id}
                  onClick={onRouting}
                  title={`${e.name} is routed by role — change it in Routing`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    width: '100%',
                    padding: '7px 10px',
                    borderRadius: 'var(--r-md)',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ ...NAME_TEXT, minWidth: 0 }}>{e.name}</span>
                    <span
                      style={{
                        display: 'block',
                        marginTop: 2,
                        fontSize: 'var(--fs-micro)',
                        color: 'var(--text-3)',
                      }}
                    >
                      {roles.map((r) => ROLE_WORD[r]).join(' · ')}
                    </span>
                  </span>
                  <Chip live={liveFor(e.provider)} />
                </button>
              )
            })}
          <p
            style={{
              padding: '2px 10px 6px',
              fontSize: '11px',
              lineHeight: 1.45,
              color: 'var(--text-3)',
            }}
          >
            Roles you have routed stay where they are. This moves everything that follows the
            selection.
          </p>
        </>
      )}

      {/* A radiogroup without a roving tabindex, deliberately, matching
          Setup.tsx's model list. And no <select> anywhere: nothing here may
          commit on a keystroke, so the choice is an explicit press on a named
          model that says what it did. */}
      <div role="radiogroup" aria-label="Model for the next pass">
        {SELECTABLE_MODELS.map((m) => {
          const cur = m.id === modelId
          return (
            // Every model, keyless ones INCLUDED and marked IDLE. Filtering to
            // keyed models would quietly re-point the next pass away from what
            // the user chose.
            <button
              key={m.id}
              role="radio"
              aria-checked={cur}
              ref={cur ? radioRef : undefined}
              onClick={() => onPick(m)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                width: '100%',
                padding: '8px 10px',
                borderRadius: 'var(--r-md)',
                cursor: 'pointer',
                textAlign: 'left',
                background: cur ? 'var(--surface-hover)' : 'transparent',
              }}
            >
              <span style={{ ...NAME_TEXT, flex: 1, minWidth: 0 }}>{m.name}</span>
              <Chip live={liveFor(m.provider)} />
            </button>
          )
        })}
      </div>
    </div>
  )
}

/** Whether this model's provider can answer at all. The word, not a colour. */
function Chip({ live }: { live: boolean }) {
  return (
    <span
      className="num"
      style={{
        flex: 'none',
        fontSize: 'var(--fs-nano)',
        letterSpacing: '0.12em',
        color: live ? 'var(--ink-insight)' : 'var(--text-3)',
      }}
    >
      {live ? 'LIVE' : 'IDLE'}
    </span>
  )
}
